import { API_BASE_URL } from "../config/api";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import SignatureCanvas from "react-signature-canvas";
import { DEFAULT_TEST_CERTIFICATE_SETTINGS } from "../utils/reportIdentifiers";
import { ensureLandscapeSignature, extractLandscapeSignature, rotateImageDataUrl } from "../utils/signatureUtils";

const getStoredRole = () => String(localStorage.getItem("role") || "").trim().toLowerCase();
const REPORT_DRAFT_STORAGE_PREFIX = "reportDraft_full";
const COMPONENT_SUGGESTIONS_STORAGE_KEY = "reportComponentSuggestions";

const getSignatureUrl = (signature = "") => {
  if (!signature) return "";
  if (signature.startsWith("data:") || signature.startsWith("http")) return signature;
  return `${API_BASE_URL}${signature.startsWith("/") ? signature : `/${signature}`}`;
};

const generateGeneralReportId = () => {
  const date = new Date();
  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const storageKey = `generalReportCounter_${year}-${month}`;
  const counter = Number(localStorage.getItem(storageKey) || 0) + 1;

  localStorage.setItem(storageKey, String(counter));

  return `GSR/${year}-${month}/${String(counter).padStart(3, "0")}`;
};

const normalizeComponentList = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value : [""];
  }

  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }

  return [""];
};

const normalizeAdditionalBoxes = (value) =>
  Array.isArray(value)
    ? value.map((item) => ({
        label: item?.label || item?.boxHeading || item?.heading || item?.title || "",
        value: item?.value || item?.boxValue || item?.content || item?.answer || ""
      }))
    : [];

const isOffValue = (value) => {
  const normalizedValue = String(value ?? "").replace(/\s+/g, "").toLowerCase();
  return normalizedValue === "off" || normalizedValue === "of";
};

const normalizeVoltageValue = (v) => {
  if (!v) return "";
  const str = String(v).trim();
  if (/^\d+$/.test(str)) {
    return `${str}V`;
  }
  return str;
};

const normalizeVoltageList = (arr) =>
  Array.isArray(arr) ? arr.map(normalizeVoltageValue).filter(Boolean) : [];

const parseCurrentNumber = (val) => {
  if (!val || isOffValue(val)) return null;
  const cleaned = String(val).replace(/,/g, "").replace(/[^\d.-]/g, "").trim();
  const num = Number(cleaned);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const calculateAutoMeasuredValue = (
  seconds = "",
  type = "",
  faultCurrentVal = null,
  settingVal = null,
  ratingVal = null
) => {
  const normalized = String(seconds || "").trim();
  if (type === "I" || normalized.toUpperCase() === "N/A" || normalized === "Immediate") {
    // Immediate (I3)
    const randomVal = Math.floor(Math.random() * (14 - 10 + 1)) + 10;
    return `${randomVal} ms`;
  }

  if (!normalized || isOffValue(normalized)) return "";

  const numSeconds = Number(normalized);
  if (!Number.isFinite(numSeconds) || numSeconds <= 0) {
    return "";
  }

  let targetSeconds = numSeconds;

  // For L protection (curve t = k/I^2), calculate theoretical trip time for the given fault current:
  // Nominal baseline test point is 3 * I1 = 3 * (setting * rating) where trip time = t_set.
  // For any fault current If: t = t_set * ((3 * I1) / If)^2
  if (type === "L") {
    const s = Number(settingVal);
    const r = Number(ratingVal);
    const ifault = parseCurrentNumber(faultCurrentVal);
    if (Number.isFinite(s) && s > 0 && Number.isFinite(r) && r > 0 && ifault && ifault > 0) {
      const i1 = s * r;
      const refTripCurrent = 3 * i1;
      if (refTripCurrent > 0) {
        targetSeconds = numSeconds * Math.pow(refTripCurrent / ifault, 2);
      }
    }
  }

  const targetMs = targetSeconds * 1000;
  const tolerance = targetMs * 0.05; // 5% realistic test tolerance
  const randomMs = targetMs - tolerance + Math.random() * (tolerance * 2);
  const roundedMs = Math.round(randomMs);
  return `${roundedMs} ms`;
};

const defaultLsigRows = () => [
  { type: "L", func: "I1", setting: "", seconds: "", formula: "t = k/i²", tripCurrent: "", current: "", measured: "", status: "OK" },
  { type: "S", func: "I2", setting: "", seconds: "", formula: "I = set × In", tripCurrent: "", current: "", measured: "", status: "OK" },
  { type: "I", func: "I3", setting: "", seconds: "", formula: "Immediate", tripCurrent: "", current: "", measured: "", status: "OK" },
  { type: "G", func: "I4", setting: "", seconds: "", formula: "I = set × In", tripCurrent: "", current: "", measured: "", status: "OK" }
];

const normalizeLsigRows = (lsig) => {
  const defaults = defaultLsigRows();

  return defaults.map((defaultRow, index) => ({
    ...defaultRow,
    ...(Array.isArray(lsig) && lsig[index] ? lsig[index] : {})
  }));
};

const formatCalculatedCurrent = (value) => {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
};

const calculateTripCurrentValue = (type, settingVal, ratingVal) => {
  const setting = Number(settingVal);
  const rating = Number(ratingVal);
  if (!Number.isFinite(setting) || setting <= 0 || !Number.isFinite(rating) || rating <= 0) {
    return "";
  }
  if (type === "L") {
    return 3 * (setting * rating);
  }
  return setting * rating;
};

const formatTripCurrent = (val) => {
  if (val === "OFF") return "OFF";
  const num = Number(val);
  if (!Number.isFinite(num) || num <= 0) return "";
  return new Intl.NumberFormat("en-US").format(num) + " A";
};

const recalculateBreakerLsig = (product = {}, preserveCustomTripCurrent = false) => {
  const lsigRows = normalizeLsigRows(product.lsig).map((row) => ({ ...row }));
  const rating = Number(product.rating);
  const hasRating = Number.isFinite(rating) && rating > 0;
  const lRow = lsigRows[0] || {};
  const isLSettingOff = isOffValue(lRow.setting);
  const lSetting = Number(lRow.setting);
  const hasLSetting = Number.isFinite(lSetting) && lSetting > 0;
  const longCurrentValue = hasRating && hasLSetting ? rating * lSetting : null;

  lsigRows.forEach((row, index) => {
    // Ensure formula is statically set
    if (index === 0) row.formula = "t = k/i²";
    else if (index === 1) row.formula = "I = set × In";
    else if (index === 2) row.formula = "Immediate";
    else if (index === 3) row.formula = "I = set × In";

    if (isOffValue(row.setting)) {
      row.setting = "OFF";
      row.seconds = "OFF";
      row.tripCurrent = "OFF";
      row.current = "OFF";
      row.measured = "OFF";
      row.status = "OFF";
      return;
    }

    if (!preserveCustomTripCurrent || !row.tripCurrent || row.tripCurrent === "OFF") {
      const tripCurrentVal = calculateTripCurrentValue(row.type, row.setting, rating);
      row.tripCurrent = formatTripCurrent(tripCurrentVal);
    }

    if (row.type === "I" && String(row.setting || "").trim() && !isOffValue(row.setting)) {
      if (!row.measured || row.measured === "—" || row.measured === "OFF" || isOffValue(row.measured)) {
        row.measured = calculateAutoMeasuredValue("", "I");
      }
    } else if (String(row.setting || "").trim() && !isOffValue(row.setting) && String(row.seconds || "").trim()) {
      if (!row.measured || row.measured === "—" || row.measured === "OFF" || isOffValue(row.measured)) {
        row.measured = calculateAutoMeasuredValue(row.seconds, row.type, row.tripCurrent, row.setting, rating);
      }
    }

    if (index === 0) {
      row.current = isLSettingOff ? "OFF" : formatCalculatedCurrent(longCurrentValue);
      return;
    }

    const multiplier = Number(row.setting);
    row.current =
      hasRating && Number.isFinite(multiplier) && multiplier > 0
        ? formatCalculatedCurrent(rating * multiplier)
        : "";
  });

  return {
    ...product,
    lsig: lsigRows
  };
};

const productTypeOptions = [
  { value: "breaker", label: "Breaker" },
  { value: "abb_warranty", label: "ABB Warranty" },
  { value: "others", label: "Others" }
];

const normalizeReportProductType = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "acb" || normalized === "breaker") return "breaker";
  if (normalized === "abb_warranty" || normalized === "abb warranty" || normalized === "abb") return "abb_warranty";
  if (normalized === "others") return "others";
  return "";
};

const breakerProductNameOptions = [
  { value: "acb", label: "ACB (Air circuit breaker)" },
  { value: "mccb", label: "MCCB (Molded case circuit breaker)" },
  { value: "vcb", label: "VCB (Vacuum circuit breaker)" },
  { value: "others", label: "Others" }
];

const getMakeOptions = (productName = "") => {
  const normalized = String(productName || "").trim().toLowerCase();
  if (normalized.includes("acb") || normalized === "acb") {
    return ["ABB", "Siemens", "Schneider", "L&T", "Other"];
  }
  if (normalized.includes("mccb") || normalized === "mccb") {
    return ["ABB", "Siemens", "Schneider", "L&T", "Other"];
  }
  if (normalized.includes("vcb") || normalized === "vcb") {
    return ["ABB", "Siemens", "Schneider", "megawin", "L&T", "Other"];
  }
  return [];
};

const breakerProductNames = breakerProductNameOptions
  .filter((option) => option.value !== "others")
  .reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

const getBreakerProductNameOptionValue = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lowerValue = normalized.toLowerCase();
  const matchedOption = breakerProductNameOptions.find((option) =>
    option.value !== "others" &&
    (option.value === lowerValue || option.label.toLowerCase() === lowerValue)
  );

  return matchedOption ? matchedOption.value : "others";
};

const normalizeBreakerProductName = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lowerValue = normalized.toLowerCase();
  if (breakerProductNames[lowerValue]) {
    return breakerProductNames[lowerValue];
  }

  const compactValue = lowerValue.split("(")[0].trim();
  return breakerProductNames[compactValue] || normalized;
};


const getBreakerTypeOptions = (productName = "") => {
  const normalized = String(productName || "").trim().toLowerCase();
  if (normalized.includes("acb") || normalized === "acb") {
    return ["New Emax", "Emax 2", "SACE", "Other"];
  }
  if (normalized.includes("mccb") || normalized === "mccb") {
    return ["MCCB", "Other"];
  }
  return ["New Emax", "Emax 2", "MCCB", "SACE", "Other"];
};
const frameOptions = ["E1", "E2", "E3", "E4", "E6", "E1.2", "E2.2", "E4.2", "E6.2", "FA2C"];
const breakerSubTypeOptions = {
  newEmax: ["PR121 - LI", "PR121 - LSIG", "PR122", "PR123", "Other"],
  emax2: ["Ek 2", "Ekip Touch", "Ekip Hi-Touch", "Ekip G Touch", "Ekip Synchro", "Ekip COM", "Ekip Charge", "Other"],
  mccb: ["TMD", "TMA", "Ekip DIP LSI", "Ekip DIP LSIG", "PR221/222", "PR231/232", "Other"],
  sace: ["Other"]
};
const defaultComponentSuggestions = [
  "Spring Charging Motor",
  "YO Opening Coil",
  "YC Closing Coil",
  "UV Under Voltage Coil",
  "Trip Unit",
  "Shunt Opening Coil",
  "Closing Coil",
  "Auxiliary Contact",
  "Mechanical Interlock",
  "Charging Handle",
  "Control Wiring",
  "Indication Lamp",
  "Terminal Block",
  "Breaker Handle",
  "Relay",
  "Fuse"
];

const normalizeSuggestionList = (items = []) =>
  Array.from(
    new Map(
      items
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .map((item) => [item.toLowerCase(), item])
    ).values()
  ).sort((a, b) => a.localeCompare(b));

const getStoredComponentSuggestions = () => {
  try {
    const rawValue = localStorage.getItem(COMPONENT_SUGGESTIONS_STORAGE_KEY);
    if (!rawValue) return defaultComponentSuggestions;
    const parsed = JSON.parse(rawValue);
    return normalizeSuggestionList([
      ...defaultComponentSuggestions,
      ...(Array.isArray(parsed) ? parsed : [])
    ]);
  } catch (error) {
    console.warn("Failed to read saved component suggestions", error);
    return defaultComponentSuggestions;
  }
};

const extractProductComponentSuggestions = (productList = []) =>
  normalizeSuggestionList(
    productList.flatMap((product) => [
      ...(Array.isArray(product?.compReplace) ? product.compReplace : []),
      ...(Array.isArray(product?.newComp) ? product.newComp : [])
    ])
  );

const isCustomBreakerType = (value, productName = "") => {
  const normalized = String(value || "").trim();
  return Boolean(normalized) && !getBreakerTypeOptions(productName).includes(normalized);
};

const isCustomFrame = (value) => {
  const normalized = String(value || "").trim();
  return Boolean(normalized) && !frameOptions.includes(normalized);
};

const normalizeBreakerTypeKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getBreakerSubTypeOptions = (breakerType = "") => {
  const normalized = normalizeBreakerTypeKey(breakerType);

  if (normalized === "newemax" || normalized === "emax") {
    return breakerSubTypeOptions.newEmax;
  }

  if (normalized === "emax2" || normalized === "emax20" || normalized === "2.0" || normalized === "20") {
    return breakerSubTypeOptions.emax2;
  }

  if (normalized === "mccb") {
    return breakerSubTypeOptions.mccb;
  }

  if (normalized === "sace") {
    return breakerSubTypeOptions.sace;
  }

  return [];
};

const getBreakerFrameOptions = (breakerType = "") => {
  const normalized = normalizeBreakerTypeKey(breakerType);
  if (normalized === "newemax" || normalized === "emax") {
    return ["E1", "E2", "E3", "E4", "E6", "FA2C", "Other"];
  }
  if (normalized === "emax2" || normalized === "emax20" || normalized === "2.0" || normalized === "20") {
    return ["E1.2", "E2.2", "E4.2", "E6.2", "FA2C", "Other"];
  }
  return ["E1", "E2", "E3", "E4", "E6", "E1.2", "E2.2", "E4.2", "E6.2", "FA2C", "Other"];
};

const normalizeBreakerProduct = (product = {}) =>
  recalculateBreakerLsig({
    ...product,
    id: product._id || product.id || Math.random().toString(36).substr(2, 9),
    productName: normalizeBreakerProductName(product.productName || product.product || ""),
    productNameMode: product.productNameMode || getBreakerProductNameOptionValue(product.productName || product.product || ""),
    checks: Array.isArray(product.checks) ? product.checks : [],
    motorVoltages: normalizeVoltageList(product.motorVoltages),
    yoVoltages: normalizeVoltageList(product.yoVoltages),
    ycVoltages: normalizeVoltageList(product.ycVoltages),
    uvVoltages: normalizeVoltageList(product.uvVoltages),
    motorStatus: (Array.isArray(product.motorVoltages) && product.motorVoltages.length > 0 && !product.motorStatus) ? "OK" : (product.motorStatus || ""),
    yoStatus: (Array.isArray(product.yoVoltages) && product.yoVoltages.length > 0 && !product.yoStatus) ? "OK" : (product.yoStatus || ""),
    ycStatus: (Array.isArray(product.ycVoltages) && product.ycVoltages.length > 0 && !product.ycStatus) ? "OK" : (product.ycStatus || ""),
    uvStatus: (Array.isArray(product.uvVoltages) && product.uvVoltages.length > 0 && !product.uvStatus) ? "OK" : (product.uvStatus || ""),
    motorCurrentType: product.motorCurrentType || "",
    yoCurrentType: product.yoCurrentType || "",
    ycCurrentType: product.ycCurrentType || "",
    uvCurrentType: product.uvCurrentType || "",
    neutral: product.neutral || "",
    breakerTypeOther: isCustomBreakerType(product.breakerType, product.productName || product.product)
      ? product.breakerType
      : product.breakerTypeOther || "",
    frameOther: isCustomFrame(product.frame) ? product.frame : product.frameOther || "",
    lsig: normalizeLsigRows(product.lsig),
    showLsigTable: product.showLsigTable !== undefined ? Boolean(product.showLsigTable) : true,
    showLsigMeasuredValues: product.showLsigMeasuredValues !== undefined ? Boolean(product.showLsigMeasuredValues) : true,
    additionalBoxes: normalizeAdditionalBoxes(product.additionalBoxes),
    compReplace: normalizeComponentList(product.compReplace),
    newComp: normalizeComponentList(product.newComp),
    componentsToBeReplaced: Array.isArray(product.componentsToBeReplaced)
      ? product.componentsToBeReplaced
      : (Array.isArray(product.compReplace)
          ? product.compReplace.filter(Boolean).map(name => ({
              name,
              status: "To Be Replaced",
              identifiedDate: new Date().toISOString().slice(0, 10),
              remarks: ""
            }))
          : []),
    componentsReplaced: Array.isArray(product.componentsReplaced)
      ? product.componentsReplaced
      : (Array.isArray(product.newComp)
          ? product.newComp.filter(Boolean).map(name => ({
              name,
              previousPart: "",
              newPart: "",
              quantity: 1,
              replacementDate: new Date().toISOString().slice(0, 10),
              remarks: ""
            }))
          : [])
  });

const normalizeOtherProduct = (product = {}) => ({
  ...product,
  id: product._id || product.id || Math.random().toString(36).substr(2, 9),
  additionalBoxes: normalizeAdditionalBoxes(product.additionalBoxes),
  compReplace: normalizeComponentList(product.compReplace),
  newComp: normalizeComponentList(product.newComp),
  componentsToBeReplaced: Array.isArray(product.componentsToBeReplaced)
    ? product.componentsToBeReplaced
    : (Array.isArray(product.compReplace)
        ? product.compReplace.filter(Boolean).map(name => ({
            name,
            status: "To Be Replaced",
            identifiedDate: new Date().toISOString().slice(0, 10),
            remarks: ""
          }))
        : []),
  componentsReplaced: Array.isArray(product.componentsReplaced)
    ? product.componentsReplaced
    : (Array.isArray(product.newComp)
        ? product.newComp.filter(Boolean).map(name => ({
            name,
            previousPart: "",
            newPart: "",
            quantity: 1,
            replacementDate: new Date().toISOString().slice(0, 10),
            remarks: ""
          }))
        : [])
});

const normalizeAbbWarrantyProduct = (product = {}) => ({
  ...product,
  id: product._id || product.id || Math.random().toString(36).substr(2, 9),
  category: "abb_warranty",
  natureOfComplaint: product.natureOfComplaint || product.complaint || product.issue || "",
  product: product.product || product.productName || (product.productMakeModel && !product.productMakeModel.includes(" / ") && product.productMakeModel !== "ABB" ? product.productMakeModel : "") || "",
  productName: product.productName || product.product || (product.productMakeModel && !product.productMakeModel.includes(" / ") && product.productMakeModel !== "ABB" ? product.productMakeModel : "") || "",
  productMakeModel: product.productMakeModel || [product.make, product.model].filter(Boolean).join(" / ") || product.product || product.productName || "",
  make: product.make || "ABB",
  model: product.model || "",
  panelBuilder: product.panelBuilder || product.panel_builder || product.panelMake || "",
  abbTicketNo: product.abbTicketNo || product.ticketNo || product.abbWarrantyTicketNo || "",
  serialNo: product.serialNo || "",
  additionalInfo: product.additionalInfo || "",
  additionalBoxes: normalizeAdditionalBoxes(product.additionalBoxes),
  pole: product.pole || product.numberOfPoles || "",
  frame: product.frame || "",
  rating: product.rating || "",
  frameRating: product.frameRating || "",
  installationDate: product.installationDate || product.dateOfInstallation || "",
  dateOfInstallation: product.dateOfInstallation || product.installationDate || "",
  commissioningDate: product.commissioningDate || "",
  visitDate: product.visitDate || "",
  operationsPerDay: product.operationsPerDay || product.numberOfOperationsPerDay || "",
  checks: Array.isArray(product.checks) ? product.checks : [],
  motorVoltages: normalizeVoltageList(product.motorVoltages),
  yoVoltages: normalizeVoltageList(product.yoVoltages),
  ycVoltages: normalizeVoltageList(product.ycVoltages),
  uvVoltages: normalizeVoltageList(product.uvVoltages),
  motorStatus: (Array.isArray(product.motorVoltages) && product.motorVoltages.length > 0 && !product.motorStatus) ? "OK" : (product.motorStatus || ""),
  yoStatus: (Array.isArray(product.yoVoltages) && product.yoVoltages.length > 0 && !product.yoStatus) ? "OK" : (product.yoStatus || ""),
  ycStatus: (Array.isArray(product.ycVoltages) && product.ycVoltages.length > 0 && !product.ycStatus) ? "OK" : (product.ycStatus || ""),
  uvStatus: (Array.isArray(product.uvVoltages) && product.uvVoltages.length > 0 && !product.uvStatus) ? "OK" : (product.uvStatus || ""),
  motorCurrentType: product.motorCurrentType || "",
  yoCurrentType: product.yoCurrentType || "",
  ycCurrentType: product.ycCurrentType || "",
  uvCurrentType: product.uvCurrentType || "",
  releaseType: product.releaseType || product.typeOfRelease || product.subType || "",
  connectionType: product.connectionType || product.typeOfConnection || "Busbar",
  cableBusbarSize: product.cableBusbarSize || product.size || "",
  numberOfRuns: product.numberOfRuns || product.runs || "",
  busbarSupport: product.busbarSupport || "Available as per ABB recommendation",
  mechanismDamage: product.mechanismDamage || product.visibleDamageToMechanism || "No",
  mechanismDamageDetails: product.mechanismDamageDetails || "",
  looseSprings: product.looseSprings || product.looseSpringsScrews || "No",
  looseSpringsDetails: product.looseSpringsDetails || "",
  otherVisibleDamage: product.otherVisibleDamage || "No",
  otherVisibleDamageDetails: product.otherVisibleDamageDetails || "",
  burnMarksAirVents: product.burnMarksAirVents || product.blackBurnMarksNearMCCBAirVents || "No",
  burnMarksAirVentsDetails: product.burnMarksAirVentsDetails || "",
  loadCurrent: product.loadCurrent || "",
  loadType: product.loadType || "Motor",
  loadTypeOther: product.loadTypeOther || "",
  remarks: product.remarks || "",
  resistanceRR: product.resistanceRR !== undefined ? product.resistanceRR : (product.resistanceRY || ""),
  resistanceYY: product.resistanceYY !== undefined ? product.resistanceYY : (product.resistanceYB || ""),
  resistanceBB: product.resistanceBB !== undefined ? product.resistanceBB : (product.resistanceBR || ""),
  resistanceNN: product.resistanceNN !== undefined ? product.resistanceNN : (product.resistanceRN || product.resistanceBN || ""),
  observation: product.observation || "",
  actionTaken: product.actionTaken || "",
  serviceStatus: product.serviceStatus || product.finalStatus || product.status || "Completed"
});

const normalizeAnyProduct = (product = {}) => {
  const cat = String(product?.category || "").trim().toLowerCase();
  if (cat === "breaker") return normalizeBreakerProduct(product);
  if (cat === "abb_warranty" || cat === "abb" || cat === "abbwarranty") return normalizeAbbWarrantyProduct(product);
  return normalizeOtherProduct(product);
};

const canCreateReportFromTravel = (travel) => {
  const status = String(travel?.status || "").trim().toLowerCase();
  return status === "at site" || status === "left site" || status === "completed";
};

const formatMergedSection = (title, value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return `${title}\n${normalized}`;
};

const mergeEngineerReportsForGeneralService = (service = {}, reports = []) => {
  const validReports = Array.isArray(reports) ? reports.filter(Boolean) : [];
  const baseReport = validReports[0] || {};
  const mergedProducts = validReports.flatMap((report) =>
    (Array.isArray(report.products) ? report.products : []).map((product) => ({
      ...product,
      sourceEngineer: report.reportOwnerEngineerName || report.engineer || "",
      sourceTeam: report.assignedTeam || ""
    }))
  );

  return {
    complaintRefId: service.serviceId || service.complaintRefId || "",
    customerName: service.customerName || service.company || baseReport.customerName || baseReport.company || "",
    address: service.address || baseReport.address || "",
    city: service.city || baseReport.city || "",
    engineer: validReports.map((report) => report.reportOwnerEngineerName || report.engineer).filter(Boolean).join(", "),
    contactName: service.contactName || baseReport.contactName || "",
    phone: service.phone || baseReport.phone || "",
    email: service.email || baseReport.email || "",
    visitDate: service.serviceDate || baseReport.visitDate || baseReport.date || "",
    complaint: service.complaint || baseReport.complaint || "",
    observation: validReports.map((report) =>
      formatMergedSection(report.reportOwnerEngineerName || report.engineer || "Engineer", report.observation)
    ).filter(Boolean).join("\n\n"),
    actionTaken: validReports.map((report) =>
      formatMergedSection(report.reportOwnerEngineerName || report.engineer || "Engineer", report.actionTaken)
    ).filter(Boolean).join("\n\n"),
    customerFeedback: validReports.map((report) =>
      formatMergedSection(report.reportOwnerEngineerName || report.engineer || "Engineer", report.customerFeedback)
    ).filter(Boolean).join("\n\n"),
    finalStatus: "Pending",
    products: mergedProducts,
    mergedFromReportIds: validReports.map((report) => report._id).filter(Boolean),
    mergedFromEngineers: validReports.map((report) => report.reportOwnerEngineerName || report.engineer).filter(Boolean)
  };
};

const buildReportDraftStorageKey = ({
  isAdminMode,
  routeId,
  reportDocId,
  selectedComplaintId,
  complaintRefId
}) => {
  const scopeId =
    reportDocId ||
    routeId ||
    selectedComplaintId ||
    complaintRefId ||
    "new";
  const mode = isAdminMode ? "admin" : "engineer";
  return `${REPORT_DRAFT_STORAGE_PREFIX}:${mode}:${scopeId}`;
};

const parseStoredReportDraft = (storageKey) => {
  try {
    if (!storageKey) return null;

    const rawDraft = localStorage.getItem(storageKey);
    if (!rawDraft) return null;

    const parsedDraft = JSON.parse(rawDraft);
    return parsedDraft && typeof parsedDraft === "object" ? parsedDraft : null;
  } catch (error) {
    console.warn("Failed to parse saved report draft", error);
    return null;
  }
};

function SignaturePadModal({
  title,
  existingSignature,
  onClose,
  onSave
}) {
  const modalSignRef = useRef();
  const [currentSignature, setCurrentSignature] = useState(existingSignature || "");

  const handleSave = async () => {
    if (!modalSignRef.current || modalSignRef.current.isEmpty()) {
      if (currentSignature) {
        const landscapeSig = await ensureLandscapeSignature(currentSignature);
        onSave(landscapeSig);
      } else {
        onSave("");
      }
      return;
    }

    const signature = await extractLandscapeSignature(modalSignRef, currentSignature);
    onSave(signature);
  };

  const handleRotate = async () => {
    let baseData = "";
    if (modalSignRef.current && !modalSignRef.current.isEmpty()) {
      baseData = modalSignRef.current.getTrimmedCanvas
        ? modalSignRef.current.getTrimmedCanvas().toDataURL("image/png")
        : modalSignRef.current.getCanvas().toDataURL("image/png");
    } else if (currentSignature) {
      baseData = currentSignature;
    }
    if (!baseData) return;
    const rotated = await rotateImageDataUrl(baseData, 90);
    setCurrentSignature(rotated);
    modalSignRef.current?.clear();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-3 sm:p-6" onClick={onClose}>
      <div
        className="flex h-[min(90vh,42rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              Please sign horizontally in landscape orientation.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
          >
            Close
          </button>
        </div>

        <div className="flex-1 bg-slate-100 p-3 sm:p-6 flex flex-col justify-center">
          <div className="relative w-full h-full min-h-[220px] rounded-[24px] border-2 border-dashed border-slate-300 bg-white shadow-inner flex flex-col items-center justify-center overflow-hidden">
            {currentSignature && (modalSignRef.current?.isEmpty?.() ?? true) ? (
              <div className="flex flex-col items-center justify-center p-4 z-0">
                <img src={currentSignature} alt="Current signature" className="max-h-44 max-w-full object-contain border border-dashed rounded p-2 bg-white" />
                <p className="text-xs text-slate-400 mt-2">Current signature. Sign over it to replace, or click Rotate 90°.</p>
              </div>
            ) : null}
            <SignatureCanvas
              ref={modalSignRef}
              penColor="#0f172a"
              minWidth={1.2}
              maxWidth={3.2}
              canvasProps={{
                className: "signature-canvas h-full w-full rounded-[24px]",
                style: { touchAction: "none" }
              }}
            />
            <div className="pointer-events-none absolute bottom-8 left-8 right-8 border-b-2 border-dashed border-slate-200 text-center pb-1">
              <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-300 select-none">───── Signature Line ─────</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-between gap-3 border-t border-slate-200 px-4 py-4 sm:px-6">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                modalSignRef.current?.clear();
                setCurrentSignature("");
              }}
              className="rounded-2xl bg-red-100 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-200"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleRotate}
              className="rounded-2xl bg-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-300 flex items-center gap-1.5"
              title="Rotate signature 90 degrees clockwise"
            >
              Rotate 90°
            </button>
          </div>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-2xl bg-blue-700 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800 shadow-md"
          >
            Save Signature
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;
  const searchParams = new URLSearchParams(location.search);
  const generalServiceId = searchParams.get("generalServiceId") || "";
  const mergeReportsParam = searchParams.get("mergeReports") || "";
  const isAdminReportsRoute =
    location.pathname.startsWith("/reports/") &&
    !location.pathname.startsWith("/reports/merge/");
  const isExplicitEngineerRoute =
    location.pathname.startsWith("/create-report") ||
    location.pathname.startsWith("/my-reports") ||
    location.pathname.startsWith("/engineer/");
  const isAdminMode =
    isAdminReportsRoute || (!isExplicitEngineerRoute && getStoredRole() === "admin");

  const customerSignRef = useRef();
  const engineerSignRef = useRef();

  const [allCustomers, setAllCustomers] = useState([]);
  const [allComplaints, setAllComplaints] = useState([]);
  const [reportId, setReportId] = useState("");
  const [reportDocId, setReportDocId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("Approval Pending");
  const [editAccessGranted, setEditAccessGranted] = useState(false);
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [existingCustomerSignature, setExistingCustomerSignature] = useState("");
  const [existingEngineerSignature, setExistingEngineerSignature] = useState("");
  const [profileEngineerSignature, setProfileEngineerSignature] = useState("");
  const [travelByComplaintId, setTravelByComplaintId] = useState({});
  const [signatureModal, setSignatureModal] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftStorageKey, setDraftStorageKey] = useState("");
  const [expandedProductIds, setExpandedProductIds] = useState([]);
  const [pendingScrollProductId, setPendingScrollProductId] = useState("");
  const draftSavedTimeoutRef = useRef(null);
  const skipLeaveProtectionRef = useRef(false);
  const productCardRefs = useRef({});
  const lookupTimers = useRef({});

  const [form, setForm] = useState({
    reportId: "",
    complaintRefId: "",
    abbTicketNo: "",
    customerName: "",
    address: "",
    city: "",
    engineer: "",
    contactName: "",
    phone: "",
    email: "",
    visitDate: "",
    complaint: "",
    observation: "",
    actionTaken: "",
    customerFeedback: "",
    finalStatus: ""
  });

  const [selectedComplaintId, setSelectedComplaintId] = useState("");
  const [productType, setProductType] = useState("");
  const [products, setProducts] = useState([]);
  const [customerProducts, setCustomerProducts] = useState([]);
  const [, setTestCertificateSettings] = useState(DEFAULT_TEST_CERTIFICATE_SETTINGS);
  const [generalService, setGeneralService] = useState(null);
  const [dynamicTeams, setDynamicTeams] = useState([]);

  const [componentSuggestionOptions, setComponentSuggestionOptions] = useState(
    () => getStoredComponentSuggestions()
  );

  const panelAreaSuggestions = useMemo(() => {
    const list = [];
    (products || []).forEach((prod) => {
      if (prod?.panelArea && typeof prod.panelArea === "string" && prod.panelArea.trim()) {
        list.push(prod.panelArea.trim());
      }
    });
    (customerProducts || []).forEach((cp) => {
      const area = cp?.product?.panelArea || cp?.panelArea;
      if (area && typeof area === "string" && area.trim()) {
        list.push(area.trim());
      }
    });
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [products, customerProducts]);

  const isAbbWarrantyComplaint = useMemo(() => {
    const selectedComp = allComplaints.find(
      (c) =>
        String(c._id) === String(selectedComplaintId) ||
        String(c.complaintId) === String(selectedComplaintId) ||
        String(c.complaintId) === String(form.complaintRefId) ||
        String(c._id) === String(id) ||
        String(c.complaintId) === String(id)
    );
    return Boolean(
      selectedComp?.isAbbWarranty ||
      selectedComp?.type === "abb_warranty" ||
      selectedComp?.category === "abb_warranty" ||
      selectedComp?.productType === "ABB Warranty" ||
      (selectedComp?.complaintId && String(selectedComp.complaintId).toUpperCase().includes("ABB")) ||
      String(form.complaintRefId || "").toUpperCase().startsWith("ABB-") ||
      String(form.complaintRefId || "").toUpperCase().includes("ABB") ||
      productType === "abb_warranty" ||
      products?.some((p) => p.category === "abb_warranty")
    );
  }, [allComplaints, form.complaintRefId, id, productType, products, selectedComplaintId]);

  const activeProductTypeOptions = useMemo(() => {
    if (isAbbWarrantyComplaint) {
      return productTypeOptions.filter((option) => option.value === "abb_warranty");
    }
    return productTypeOptions;
  }, [isAbbWarrantyComplaint]);
  const accessoryOptions = [
    {
      label: "Spring Charging Motor ",
      field: "motorVoltages",
      statusField: "motorStatus",
      typeField: "motorCurrentType",
      options: ["24V", "48V", "110V", "230V"]
    },
    {
      label: "YO Opening Coil",
      field: "yoVoltages",
      statusField: "yoStatus",
      typeField: "yoCurrentType",
      options: ["24V", "48V", "110V", "230V"]
    },
    {
      label: "YC Closing Coil",
      field: "ycVoltages",
      statusField: "ycStatus",
      typeField: "ycCurrentType",
      options: ["24V", "48V", "110V", "230V"]
    },
    {
      label: "UV Under Voltage Coil",
      field: "uvVoltages",
      statusField: "uvStatus",
      typeField: "uvCurrentType",
      options: ["24V", "48V", "110V", "230V", "380V", "415V", "440V", "460V", "480V"]
    }
  ];

  const formatAddress = (address) => {
    if (!address) return "";

    if (typeof address === "string") {
      return address;
    }

    return [
      address.street,
      address.area,
      address.city,
      address.state,
      address.pincode
    ]
      .filter(Boolean)
      .join(", ");
  };


  const currentDraftStorageKey = useMemo(
    () =>
      buildReportDraftStorageKey({
        isAdminMode,
        routeId: id,
        reportDocId,
        selectedComplaintId,
        complaintRefId: form.complaintRefId
      }),
    [form.complaintRefId, id, isAdminMode, reportDocId, selectedComplaintId]
  );

  const toInputDate = (value) => {
    if (!value) return "";

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (isoDate.test(String(value))) return String(value);

    const ddmmyyyy = /^(\d{2})[-/](\d{2})[-/](\d{4})$/;
    const match = String(value).match(ddmmyyyy);

    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}`;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toISOString().slice(0, 10);
  };

  const fromInputDate = (value) => {
    if (!value) return "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return value;
    const [year, month, day] = value.split("-");
    return `${day}-${month}-${year}`;
  };

const createBreakerProduct = (productName = "") => ({
    id: Math.random().toString(36).substr(2, 9),
    category: "breaker",
    productName: normalizeBreakerProductName(productName),
    productNameMode: getBreakerProductNameOptionValue(productName),
    serialNo: "",
    make: "",
    model: "",
    rating: "",
    pole: "",
    neutral: "",
    feeder: "",
    panelArea: "",
    breakerType: "",
    breakerTypeOther: "",
    subTypeOther: "",
    releaseSerialNumber: "",
    frame: "",
    frameOther: "",
    subType: "",
    warranty: "",
    remarks: "",
    compReplace: [""],
    newComp: [""],
    additionalInfo: "",
    serviceStatus: "",
    checks: [],
    motorVoltages: [],
    motorStatus: "",
    motorCurrentType: "",
    yoVoltages: [],
    yoStatus: "",
    yoCurrentType: "",
    ycVoltages: [],
    ycStatus: "",
    ycCurrentType: "",
    uvVoltages: [],
    uvStatus: "",
    uvCurrentType: "",
    lsig: defaultLsigRows(),
    showLsigTable: false,
    showLsigMeasuredValues: false,
    additionalBoxes: []
  });

  const createAbbWarrantyProduct = (natureOfComplaint = "") => ({
    id: Math.random().toString(36).substr(2, 9),
    category: "abb_warranty",
    natureOfComplaint: natureOfComplaint || "",
    product: "",
    productName: "",
    productMakeModel: "",
    make: "ABB",
    model: "",
    panelBuilder: "",
    abbTicketNo: "",
    serialNo: "",
    additionalInfo: "",
    additionalBoxes: [],
    pole: "",
    frame: "",
    rating: "",
    frameRating: "",
    installationDate: "",
    dateOfInstallation: "",
    commissioningDate: "",
    visitDate: "",
    operationsPerDay: "",
    checks: [],
    motorVoltages: [],
    motorStatus: "",
    motorCurrentType: "",
    yoVoltages: [],
    yoStatus: "",
    yoCurrentType: "",
    ycVoltages: [],
    ycStatus: "",
    ycCurrentType: "",
    uvVoltages: [],
    uvStatus: "",
    uvCurrentType: "",
    releaseType: "",
    connectionType: "Busbar",
    cableBusbarSize: "",
    numberOfRuns: "",
    busbarSupport: "Available as per ABB recommendation",
    mechanismDamage: "No",
    mechanismDamageDetails: "",
    looseSprings: "No",
    looseSpringsDetails: "",
    otherVisibleDamage: "No",
    otherVisibleDamageDetails: "",
    burnMarksAirVents: "No",
    burnMarksAirVentsDetails: "",
    loadCurrent: "",
    loadType: "Motor",
    loadTypeOther: "",
    remarks: "",
    resistanceRR: "",
    resistanceYY: "",
    resistanceBB: "",
    resistanceNN: "",
    observation: "",
    actionTaken: "",
    serviceStatus: "Completed"
  });

  const createOtherProduct = () => ({
    id: Math.random().toString(36).substr(2, 9),
    category: "others",
    product: "",
    make: "",
    model: "",
    serialNo: "",
    pole: "",
    rating: "",
    voltage: "",
    speed: "",
    warranty: "",
    panelArea: "",
    observation: "",
    actionTaken: "",
    status: "",
    additionalInfo: "",
    additionalBoxes: [],
    compReplace: [""],
    newComp: [""]
  });

  const fillFormFromComplaint = useCallback((complaint, customers) => {
    if (!complaint) return;

    const matchedCustomer = customers.find(
      (customer) => String(customer._id) === String(complaint.customerId)
    );

    setSelectedComplaintId(complaint._id);

    const isAbb = Boolean(
      complaint.isAbbWarranty ||
      complaint.productType === "ABB Warranty" ||
      (complaint.complaintId && String(complaint.complaintId).includes("ABB"))
    );

    if (matchedCustomer?._id) {
      const token = localStorage.getItem("token");
      axios
        .get(API_BASE_URL + `/api/customers/${matchedCustomer._id}/products`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        })
        .then((res) => {
          setCustomerProducts(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => {
          console.error("Failed to load customer products for autocomplete:", err);
        });
    }

    setForm((prev) => ({
      ...prev,
      complaintRefId: complaint.complaintId || complaint._id || "",
      abbTicketNo: complaint.abbTicketNo || prev.abbTicketNo || "",
      customerName: matchedCustomer?.name || complaint.company || "",
      address: formatAddress(matchedCustomer?.address) || complaint.address || "",
      city: matchedCustomer?.address?.city || complaint.city || "",
      visitDate: toInputDate(complaint.visitDate || complaint.date || complaint.serviceDate || prev.visitDate),
      contactName:
        complaint.contactName || matchedCustomer?.contactPerson || "",
      phone: complaint.contactPhone || matchedCustomer?.phone || "",
      email: complaint.contactEmail || matchedCustomer?.email || "",
      complaint: complaint.issue || ""
    }));

    if (isAbb) {
      setProductType("abb_warranty");
      setProducts((prev) => {
        if (!prev || prev.length === 0) {
          const abbProd = createAbbWarrantyProduct(complaint.issue || "");
          abbProd.abbTicketNo = complaint.abbTicketNo || "";
          abbProd.visitDate = toInputDate(complaint.visitDate || complaint.date || "");
          return [abbProd];
        }
        return prev.map(p => p.category === "abb_warranty" ? { ...p, abbTicketNo: p.abbTicketNo || complaint.abbTicketNo || "" } : p);
      });
    }
  }, []);



  const moveProduct = (index, direction) => {
    const updated = [...products];
    if (direction === "up" && index > 0) {
      const temp = updated[index];
      updated[index] = updated[index - 1];
      updated[index - 1] = temp;
    } else if (direction === "down" && index < updated.length - 1) {
      const temp = updated[index];
      updated[index] = updated[index + 1];
      updated[index + 1] = temp;
    }
    setProducts(updated);
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const [custRes, compRes, reportRes, travelRes, testCertificateSettingsRes, generalServiceRes, engineerProfileRes, systemSettingsRes] = await Promise.all([
          axios.get(API_BASE_URL + "/api/customers", { headers }),
          isAdminMode
            ? axios.get(API_BASE_URL + "/api/complaints", { headers })
            : engineerName
            ? axios.get(
                API_BASE_URL + `/api/complaints/engineer/${engineerLookup}`,
                { headers }
              )
            : Promise.resolve({ data: [] }),
          id
            ? axios
                .get(API_BASE_URL + `/api/visits/${id}`, { headers })
                .catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          axios.get(API_BASE_URL + "/api/travel", { headers }).catch(() => ({ data: [] })),
          axios.get(API_BASE_URL + "/api/test-certificate-settings", { headers }).catch(() => ({ data: DEFAULT_TEST_CERTIFICATE_SETTINGS })),
          generalServiceId
            ? axios.get(API_BASE_URL + `/api/general-services/${generalServiceId}`, {
                headers,
                params: isAdminMode ? undefined : { engineer: engineerLookup }
              }).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          !isAdminMode && engineerLookup
            ? axios.get(API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`, { headers }).catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
          axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers }).catch(() => ({ data: null }))
        ]);

        const customers = Array.isArray(custRes.data) ? custRes.data : [];
        const complaints = Array.isArray(compRes.data) ? compRes.data : [];
        const travels = Array.isArray(travelRes.data) ? travelRes.data : [];
        const certificateSettings = testCertificateSettingsRes?.data || DEFAULT_TEST_CERTIFICATE_SETTINGS;
        const selectedGeneralService = generalServiceRes?.data || null;
        const profileSignature = engineerProfileRes?.data?.signature || "";
        const systemSettings = systemSettingsRes?.data || null;
        
        const teamsList = systemSettings && Array.isArray(systemSettings.teams) ? systemSettings.teams : [
          { id: "team_1", name: "Team 1" },
          { id: "team_2", name: "Team 2" },
          { id: "team_3", name: "Team 3" }
        ];
        setDynamicTeams(teamsList);

        const availableComplaints = isAdminMode
          ? complaints
          : complaints.filter((complaint) =>
              complaint.assignmentStatus === "accepted" ||
              String(complaint._id) === String(id) ||
              String(complaint.complaintId) === String(id)
            );
        const existingReport = reportRes.data;

        setAllCustomers(customers);
        setAllComplaints(availableComplaints);
        setProfileEngineerSignature(profileSignature);
        setGeneralService(selectedGeneralService);
        setTestCertificateSettings({
          ...DEFAULT_TEST_CERTIFICATE_SETTINGS,
          ...certificateSettings,
          monthCodes: {
            ...DEFAULT_TEST_CERTIFICATE_SETTINGS.monthCodes,
            ...(certificateSettings?.monthCodes || {})
          },
          materialCodes: {
            ...DEFAULT_TEST_CERTIFICATE_SETTINGS.materialCodes,
            ...(certificateSettings?.materialCodes || {})
          }
        });
        setTravelByComplaintId(
          travels.reduce((acc, travel) => {
            [travel.complaintId, travel.complaintRefId]
              .filter(Boolean)
              .forEach((key) => {
                const normalizedKey = String(key);
                const current = acc[normalizedKey];
                if (
                  !current ||
                  new Date(travel.updatedAt || travel.createdAt || 0) >
                    new Date(current.updatedAt || current.createdAt || 0)
                ) {
                  acc[normalizedKey] = travel;
                }
              });
            return acc;
          }, {})
        );

        if (existingReport) {
          const linkedComplaint = availableComplaints.find((complaint) => {
            const visitComplaint = String(existingReport.complaintId || "").trim();
            return (
              String(complaint._id || "") === visitComplaint ||
              String(complaint.complaintId || "") === visitComplaint
            );
          });

          const linkedCustomer = customers.find(
            (customer) =>
              String(customer._id || "") ===
              String(linkedComplaint?.customerId || "")
          );

          if (linkedCustomer?._id) {
            axios
              .get(API_BASE_URL + `/api/customers/${linkedCustomer._id}/products`, { headers })
              .then((res) => {
                setCustomerProducts(Array.isArray(res.data) ? res.data : []);
              })
              .catch((err) => {
                console.error("Failed to load customer products for autocomplete:", err);
              });
          }
          const existingDraftKey = buildReportDraftStorageKey({
            isAdminMode,
            routeId: id,
            reportDocId: existingReport._id || id,
            selectedComplaintId:
              linkedComplaint?._id || existingReport.complaintId || "",
            complaintRefId:
              linkedComplaint?.complaintId || existingReport.complaintId || ""
          });
          const savedDraft = parseStoredReportDraft(existingDraftKey);
          let useDraft = false;
          if (savedDraft) {
            useDraft = window.confirm(
              "An unsaved local draft was found on this device for this report. Would you like to restore it? Click Cancel to load the latest synced data from the database."
            );
            if (!useDraft) {
              localStorage.removeItem(existingDraftKey);
            }
          }
          const restoredProducts = (useDraft && Array.isArray(savedDraft?.products))
            ? savedDraft.products.map((product) => normalizeAnyProduct(product))
            : null;

          setReportDocId(existingReport._id || "");
          setReportId(existingReport.reportId || "");
          setSelectedComplaintId(
            (useDraft && savedDraft?.selectedComplaintId) ||
              linkedComplaint?._id ||
              existingReport.complaintId ||
              ""
          );
          setProducts(
            restoredProducts ||
              (Array.isArray(existingReport.products)
              ? existingReport.products.map((product) => normalizeAnyProduct(product))
              : [])
          );
          setExistingCustomerSignature(
            (useDraft && typeof savedDraft?.existingCustomerSignature === "string")
              ? savedDraft.existingCustomerSignature
              : existingReport.customerSignature || ""
          );
          setExistingEngineerSignature(
            (useDraft && typeof savedDraft?.existingEngineerSignature === "string")
              ? savedDraft.existingEngineerSignature
              : existingReport.engineerSignature || profileSignature || ""
          );
          setApprovalStatus(
            (useDraft && typeof savedDraft?.approvalStatus === "string" && savedDraft.approvalStatus)
              ? savedDraft.approvalStatus
              : existingReport.approvalStatus || "Approval Pending"
          );
          setEditAccessGranted(
            existingReport.editAccessGranted || false
          );
          setReviewRemarks(
            (useDraft && typeof savedDraft?.reviewRemarks === "string")
              ? savedDraft.reviewRemarks
              : existingReport.reviewRemarks || ""
          );
          setForm({
            reportId:
              (useDraft && savedDraft?.form?.reportId) ||
              existingReport.reportId ||
              "",
            complaintRefId:
              (useDraft && savedDraft?.form?.complaintRefId) ||
              linkedComplaint?.complaintId ||
              existingReport.complaintId ||
              "",
            abbTicketNo:
              (useDraft && savedDraft?.form?.abbTicketNo) ||
              existingReport.abbTicketNo ||
              linkedComplaint?.abbTicketNo ||
              existingReport.products?.[0]?.abbTicketNo ||
              "",
            customerName:
              (useDraft && savedDraft?.form?.customerName) ||
              existingReport.customerName ||
              linkedCustomer?.name ||
              existingReport.company ||
              "",
            address:
              (useDraft && savedDraft?.form?.address) ||
              existingReport.address || formatAddress(linkedCustomer?.address),
            city:
              (useDraft && savedDraft?.form?.city) ||
              existingReport.city ||
              linkedCustomer?.address?.city ||
              "",
            engineer:
              (useDraft && savedDraft?.form?.engineer) ||
              existingReport.engineer ||
              engineerName ||
              "",
            contactName:
              (useDraft && savedDraft?.form?.contactName) ||
              existingReport.contactName ||
              linkedComplaint?.contactName ||
              linkedCustomer?.contactPerson ||
              "",
            phone:
              (useDraft && savedDraft?.form?.phone) ||
              existingReport.phone ||
              linkedComplaint?.contactPhone ||
              linkedCustomer?.phone ||
              "",
            email:
              (useDraft && savedDraft?.form?.email) ||
              existingReport.email ||
              linkedCustomer?.email ||
              "",
            visitDate: toInputDate(
              (useDraft && savedDraft?.form?.visitDate) ||
                existingReport.visitDate ||
                existingReport.date ||
                linkedComplaint?.visitDate
            ),
            complaint:
              (useDraft && savedDraft?.form?.complaint) ||
              existingReport.complaint || linkedComplaint?.issue || "",
            observation:
              (useDraft && savedDraft?.form?.observation) ||
              existingReport.observation ||
              "",
            actionTaken:
              (useDraft && savedDraft?.form?.actionTaken) ||
              existingReport.actionTaken ||
              "",
            customerFeedback:
              (useDraft && savedDraft?.form?.customerFeedback) ||
              existingReport.customerFeedback ||
              "",
            finalStatus:
              (useDraft && savedDraft?.form?.finalStatus) ||
              existingReport.finalStatus ||
              "Pending"
          });
          setProductType(
            (useDraft && typeof savedDraft?.productType === "string") ? normalizeReportProductType(savedDraft.productType) : ""
          );
          setDraftStorageKey(existingDraftKey);
          setLoading(false);
          return;
        }

        if (selectedGeneralService) {
          const generatedId = generateGeneralReportId();
          const selectedMergeIds = mergeReportsParam
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);
          const selectedMergeReports = selectedMergeIds.length
            ? (Array.isArray(selectedGeneralService.reports) ? selectedGeneralService.reports : []).filter((report) =>
                selectedMergeIds.includes(String(report._id))
              )
            : [];
          const mergedDraft = selectedMergeReports.length
            ? mergeEngineerReportsForGeneralService(selectedGeneralService, selectedMergeReports)
            : null;
          const myAssignment = !isAdminMode
            ? (Array.isArray(selectedGeneralService.assignedEngineers)
                ? selectedGeneralService.assignedEngineers.find((assignment) =>
                    [assignment.engineerId, assignment.engineerName, assignment.engineerEmail]
                      .filter(Boolean)
                      .some((value) => String(value).trim().toLowerCase() === String(engineerLookup || engineerName).trim().toLowerCase())
                  )
                : null)
            : null;

          setReportId(generatedId);
          setApprovalStatus("Approval Pending");
          setReviewRemarks("");
          setSelectedComplaintId("");
          setForm((prev) => ({
            ...prev,
            reportId: generatedId,
            complaintRefId: mergedDraft?.complaintRefId || selectedGeneralService.serviceId || "",
            customerName: mergedDraft?.customerName || selectedGeneralService.customerName || selectedGeneralService.company || "",
            address: mergedDraft?.address || selectedGeneralService.address || "",
            city: mergedDraft?.city || selectedGeneralService.city || "",
            engineer: mergedDraft?.engineer || (!isAdminMode ? (myAssignment?.engineerName || engineerName || "") : ""),
            contactName: mergedDraft?.contactName || selectedGeneralService.contactName || "",
            phone: mergedDraft?.phone || selectedGeneralService.phone || "",
            email: mergedDraft?.email || selectedGeneralService.email || "",
            visitDate: toInputDate(mergedDraft?.visitDate || selectedGeneralService.serviceDate || ""),
            complaint: mergedDraft?.complaint || selectedGeneralService.complaint || "",
            observation: mergedDraft?.observation || selectedGeneralService.observation || "",
            actionTaken: mergedDraft?.actionTaken || selectedGeneralService.actionTaken || "",
            customerFeedback: mergedDraft?.customerFeedback || "",
            finalStatus: mergedDraft?.finalStatus || "Pending"
          }));
          setProducts(
            (mergedDraft?.products || []).map((product) => normalizeAnyProduct(product))
          );
          setDraftStorageKey(buildReportDraftStorageKey({
            isAdminMode,
            routeId: id,
            reportDocId: "",
            selectedComplaintId: "",
            complaintRefId: selectedGeneralService.serviceId || ""
          }));
          setLoading(false);
          return;
        }

        const generatedId = generateGeneralReportId();
        setReportId(generatedId);
        setApprovalStatus("Approval Pending");
        setReviewRemarks("");
        setForm((prev) => ({ ...prev, reportId: generatedId }));

        const nextDraftKey = buildReportDraftStorageKey({
          isAdminMode,
          routeId: id,
          reportDocId: "",
          selectedComplaintId: "",
          complaintRefId: ""
        });
        const savedDraft = parseStoredReportDraft(nextDraftKey);
        let useDraft = false;
        if (savedDraft) {
          useDraft = window.confirm(
            "An unsaved local draft was found on this device. Would you like to restore it?"
          );
          if (!useDraft) {
            localStorage.removeItem(nextDraftKey);
          }
        }
        setDraftStorageKey(nextDraftKey);

        if (useDraft && savedDraft) {
          const restoredForm = savedDraft.form && typeof savedDraft.form === "object"
            ? savedDraft.form
            : null;
          const restoredProducts = Array.isArray(savedDraft.products)
            ? savedDraft.products.map((product) => normalizeAnyProduct(product))
            : [];

          if (savedDraft.reportId) {
            setReportId(savedDraft.reportId);
          }

          if (savedDraft.selectedComplaintId) {
            setSelectedComplaintId(savedDraft.selectedComplaintId);
          }

          if (typeof savedDraft.productType === "string") {
            setProductType(normalizeReportProductType(savedDraft.productType));
          }

          if (typeof savedDraft.approvalStatus === "string" && savedDraft.approvalStatus) {
            setApprovalStatus(savedDraft.approvalStatus);
          }

          if (typeof savedDraft.reviewRemarks === "string") {
            setReviewRemarks(savedDraft.reviewRemarks);
          }

          if (typeof savedDraft.existingCustomerSignature === "string") {
            setExistingCustomerSignature(savedDraft.existingCustomerSignature);
          }

          if (typeof savedDraft.existingEngineerSignature === "string") {
            setExistingEngineerSignature(savedDraft.existingEngineerSignature);
          } else {
            setExistingEngineerSignature(profileSignature || "");
          }

          if (restoredForm) {
            setForm((prev) => ({
              ...prev,
              ...restoredForm,
              reportId: restoredForm.reportId || savedDraft.reportId || generatedId
            }));
          }

          setProducts(restoredProducts);
        } else {
          const initialComplaint =
            availableComplaints.find((complaint) => String(complaint._id) === String(id)) ||
            availableComplaints[0];

          if (initialComplaint) {
            fillFormFromComplaint(initialComplaint, customers);
          }
          setExistingEngineerSignature(profileSignature || "");
        }
      } catch (err) {
        console.error("Error fetching initial data", err);
      } finally {
        setDraftReady(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [engineerLookup, engineerName, fillFormFromComplaint, generalServiceId, id, isAdminMode, mergeReportsParam]);

  useEffect(() => {
    if (!currentDraftStorageKey) return;
    setDraftStorageKey(currentDraftStorageKey);
  }, [currentDraftStorageKey]);

  useEffect(() => {
    if (!products.length) {
      setExpandedProductIds([]);
      return;
    }

    setExpandedProductIds((current) => {
      const validIds = current.filter((productId) =>
        products.some((product) => product.id === productId)
      );

      if (!validIds.length) {
        return [products[products.length - 1].id];
      }

      return validIds;
    });
  }, [products]);

  useEffect(() => {
    if (!pendingScrollProductId) return;

    const targetCard = productCardRefs.current[pendingScrollProductId];
    if (!targetCard) return;

    const timer = window.setTimeout(() => {
      targetCard.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      setPendingScrollProductId("");
    }, 120);

    return () => window.clearTimeout(timer);
  }, [expandedProductIds, pendingScrollProductId, products]);

  useEffect(() => {
    if (!draftReady || loading || !draftStorageKey) {
      return;
    }

    try {
      const draftPayload = {
        form,
        products,
        selectedComplaintId,
        productType,
        reportId,
        approvalStatus,
        reviewRemarks,
        existingCustomerSignature,
        existingEngineerSignature,
      };

      localStorage.setItem(draftStorageKey, JSON.stringify(draftPayload));
      setDraftSaved(true);

      if (draftSavedTimeoutRef.current) {
        clearTimeout(draftSavedTimeoutRef.current);
      }

      draftSavedTimeoutRef.current = window.setTimeout(() => {
        setDraftSaved(false);
      }, 1000);
    } catch (error) {
      console.warn("Failed to save report draft locally", error);
    }
  }, [
    approvalStatus,
    draftReady,
    existingCustomerSignature,
    existingEngineerSignature,
    form,
    loading,
    productType,
    products,
    draftStorageKey,
    reportDocId,
    reportId,
    reviewRemarks,
    selectedComplaintId
  ]);

  useEffect(() => () => {
    if (draftSavedTimeoutRef.current) {
      clearTimeout(draftSavedTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!draftReady || loading || !draftStorageKey || skipLeaveProtectionRef.current) {
      return;
    }

    const handleBeforeUnload = (event) => {
      if (skipLeaveProtectionRef.current) return;

      event.preventDefault();
      event.returnValue = "";
    };

    const handlePopState = () => {
      if (skipLeaveProtectionRef.current) return;

      const shouldLeave = window.confirm("You have unsaved report changes. Do you want to leave this page?");
      if (shouldLeave) {
        skipLeaveProtectionRef.current = true;
        navigate(-1);
        return;
      }

      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [draftReady, draftStorageKey, loading, navigate]);

  const handleDateChange = (e) => {
    const dateVal = e.target.value;
    setForm({ ...form, visitDate: dateVal });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const selectComplaint = (comp) => {
    fillFormFromComplaint(comp, allCustomers);
  };

  const addProduct = () => {
    if (!productType) return;

    let nextProduct;
    if (productType === "abb_warranty") {
      nextProduct = createAbbWarrantyProduct(form.complaint || "");
    } else if (productType === "breaker" || breakerProductNames[productType]) {
      nextProduct = createBreakerProduct(breakerProductNames[productType] || "");
    } else {
      nextProduct = createOtherProduct();
    }

    setProducts([...products, nextProduct]);
    setExpandedProductIds([nextProduct.id]);
    setPendingScrollProductId(nextProduct.id);
  };

  const deleteProduct = (index) => {
    const productLabel = `Product ${index + 1}`;
    const confirmed = window.confirm(
      `Are you sure you want to remove ${productLabel}? This will delete the entered details for this product.`
    );

    if (!confirmed) return;

    const updated = products.filter((_, i) => i !== index);
    setProducts(updated);
  };

  const handleProductChange = (i, field, value) => {
    const updated = [...products];

    if (field === "serialNo") {
      updated[i].serialNo = value;
      setProducts(updated);
      handleSerialChangeDebounced(i, value);
      return;
    }

    if (field === "productNameOption") {
      updated[i].productNameMode = value;
      updated[i].productName = breakerProductNames[value] || "";
      updated[i].make = "";
      updated[i].makeOther = "";
      updated[i].breakerType = "";
      updated[i].breakerTypeOther = "";
      updated[i].model = "";
      updated[i].modelOther = "";
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      setProducts(updated);
      return;
    }

    if (field === "productName") {
      updated[i].productName = value;
      updated[i].make = "";
      updated[i].makeOther = "";
      updated[i].breakerType = "";
      updated[i].breakerTypeOther = "";
      updated[i].model = "";
      updated[i].modelOther = "";
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      setProducts(updated);
      return;
    }

    if (field === "showLsigTable") {
      updated[i].showLsigTable = Boolean(value);
      if (!value) {
        updated[i].showLsigMeasuredValues = false;
      }
      setProducts(updated);
      return;
    }

    if (field === "showLsigMeasuredValues") {
      updated[i].showLsigMeasuredValues = Boolean(value);
      if (value) {
        updated[i].showLsigTable = true;
      }
      setProducts(updated);
      return;
    }

    if (field === "make") {
      updated[i].make = value === "Other" ? updated[i].makeOther || "" : value;
      updated[i].makeOther = value === "Other" ? updated[i].makeOther || "" : "";
      setProducts(updated);
      return;
    }

    if (field === "makeOther") {
      updated[i].makeOther = value;
      updated[i].make = value;
      setProducts(updated);
      return;
    }

    if (field === "breakerType") {
      updated[i].breakerType = value === "Other" ? updated[i].breakerTypeOther || "" : value;
      updated[i].breakerTypeOther = value === "Other" ? updated[i].breakerTypeOther || "" : "";
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      updated[i].model = "";
      updated[i].modelOther = "";
      setProducts(updated);
      return;
    }

    if (field === "breakerTypeOther") {
      updated[i].breakerTypeOther = value;
      updated[i].breakerType = value;
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      updated[i].model = "";
      updated[i].modelOther = "";
      setProducts(updated);
      return;
    }

    if (field === "model") {
      updated[i].model = value === "Other" ? updated[i].modelOther || "" : value;
      updated[i].modelOther = value === "Other" ? updated[i].modelOther || "" : "";
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      setProducts(updated);
      return;
    }

    if (field === "modelOther") {
      updated[i].modelOther = value;
      updated[i].model = value;
      updated[i].subType = "";
      updated[i].subTypeOther = "";
      setProducts(updated);
      return;
    }

    if (field === "frame") {
      updated[i].frame = value === "Other" ? updated[i].frameOther || "" : value;
      updated[i].frameOther = value === "Other" ? updated[i].frameOther || "" : "";
      setProducts(updated);
      return;
    }

    if (field === "frameOther") {
      updated[i].frameOther = value;
      updated[i].frame = value;
      setProducts(updated);
      return;
    }

    if (field === "subType") {
      updated[i].subType = value === "Other" ? updated[i].subTypeOther || "" : value;
      updated[i].subTypeOther = value === "Other" ? updated[i].subTypeOther || "" : "";
      setProducts(updated);
      return;
    }

    if (field === "subTypeOther") {
      updated[i].subTypeOther = value;
      updated[i].subType = value;
      setProducts(updated);
      return;
    }

    updated[i][field] = value;
    if (field === "rating" && updated[i]?.category === "breaker") {
      updated[i] = recalculateBreakerLsig(updated[i]);
    }
    setProducts(updated);
  };

  const addAdditionalBox = (productIndex) => {
    const updated = [...products];
    updated[productIndex].additionalBoxes = [
      ...(Array.isArray(updated[productIndex].additionalBoxes) ? updated[productIndex].additionalBoxes : []),
      { label: "", value: "" }
    ];
    setProducts(updated);
  };

  const updateAdditionalBox = (productIndex, boxIndex, field, value) => {
    const updated = [...products];
    const boxes = Array.isArray(updated[productIndex].additionalBoxes)
      ? [...updated[productIndex].additionalBoxes]
      : [];
    boxes[boxIndex] = {
      ...(boxes[boxIndex] || { label: "", value: "" }),
      [field]: value
    };
    updated[productIndex].additionalBoxes = boxes;
    setProducts(updated);
  };

  const removeAdditionalBox = (productIndex, boxIndex) => {
    const updated = [...products];
    updated[productIndex].additionalBoxes = (updated[productIndex].additionalBoxes || []).filter((_, index) => index !== boxIndex);
    setProducts(updated);
  };

  const toggleProductExpanded = (productId) => {
    setExpandedProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [productId]
    );
  };

  const handleCheckbox = (i, value) => {
    const updated = [...products];
    const arr = updated[i].checks || [];
    updated[i].checks = arr.includes(value)
      ? arr.filter((v) => v !== value)
      : [...arr, value];

    const uncheckedAccessory = accessoryOptions.find(
      (option) => option.label === value
    );

    if (uncheckedAccessory && !updated[i].checks.includes(value)) {
      updated[i][uncheckedAccessory.field] = [];
      updated[i][uncheckedAccessory.statusField] = "";
      updated[i][uncheckedAccessory.typeField] = "";
    }

    setProducts(updated);
  };

  const handleVoltageCheckbox = (productIndex, field, value) => {
    const updated = [...products];
    const arr = Array.isArray(updated[productIndex][field])
      ? updated[productIndex][field]
      : [];
    const newVoltages = arr.includes(value) ? [] : [value];
    updated[productIndex][field] = newVoltages;

    const accessory = accessoryOptions.find((opt) => opt.field === field);
    if (accessory && newVoltages.length > 0) {
      updated[productIndex][accessory.statusField] = "OK";
    }

    setProducts(updated);
  };

  const handleAccessoryCurrentTypeChange = (productIndex, field, value) => {
    const updated = [...products];
    updated[productIndex][field] = updated[productIndex][field] === value ? "" : value;
    setProducts(updated);
  };

  const renderAdditionalBoxes = (product, productIndex) => (
    <div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <label className="block text-xs font-bold uppercase text-gray-600">Additional Boxes</label>
          <p className="text-xs text-gray-500">Add a custom heading and value when this product needs an extra detail on the PDF.</p>
        </div>
        <button
          type="button"
          onClick={() => addAdditionalBox(productIndex)}
          className="self-start rounded bg-green-100 px-4 py-2 text-sm font-semibold text-green-800 transition hover:bg-green-200 sm:self-auto"
        >
          + Add Additional Box
        </button>
      </div>

      {(product.additionalBoxes || []).length > 0 && (
        <div className="mt-3 space-y-3">
          {(product.additionalBoxes || []).map((box, boxIndex) => (
            <div key={`${product.id || productIndex}-extra-${boxIndex}`} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Box Heading</label>
                  <input
                    value={box.label || ""}
                    placeholder="Example: Ambient Temperature"
                    className="w-full rounded border p-2"
                    onChange={(e) => updateAdditionalBox(productIndex, boxIndex, "label", e.target.value)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Box Value</label>
                  <input
                    value={box.value || ""}
                    placeholder="Enter value"
                    className="w-full rounded border p-2"
                    onChange={(e) => updateAdditionalBox(productIndex, boxIndex, "value", e.target.value)}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeAdditionalBox(productIndex, boxIndex)}
                className="mt-3 rounded bg-red-100 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-200"
              >
                Remove Box
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const fetchProductHistory = async (serialNo, index) => {
    if (!serialNo || String(serialNo).trim() === "") return;

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        API_BASE_URL + `/api/visits/serial-history/${encodeURIComponent(serialNo)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );

      if (res.data && res.data.found) {
        const { latestProduct, serviceHistory, componentHistory } = res.data;
        const updated = [...products];
        const prevProd = updated[index];

        const mergedProduct = {
          ...prevProd,
          ...latestProduct,
          id: prevProd.id,
          category: prevProd.category,
          
          componentsToBeReplaced: Array.isArray(latestProduct.componentsToBeReplaced)
            ? latestProduct.componentsToBeReplaced
                .filter((c) => ["to be replaced", "pending"].includes(String(c.status || "").trim().toLowerCase()))
            : (Array.isArray(latestProduct.compReplace)
                ? latestProduct.compReplace.filter(Boolean).map(name => ({
                    name,
                    status: "To Be Replaced",
                    identifiedDate: new Date().toISOString().slice(0, 10),
                    remarks: ""
                  }))
                : []),
          
          componentsReplaced: [],
          compReplace: Array.isArray(latestProduct.componentsToBeReplaced)
            ? latestProduct.componentsToBeReplaced
                .filter((c) => ["to be replaced", "pending"].includes(String(c.status || "").trim().toLowerCase()))
                .map((c) => c.name || c.componentName || "")
            : (Array.isArray(latestProduct.compReplace) ? latestProduct.compReplace : [""]),
          newComp: [""],
          
          serviceHistory: serviceHistory || [],
          componentHistory: componentHistory || [],
          hasHistory: true,
          activeHistoryTab: "service"
        };

        if (prevProd.category === "breaker") {
          updated[index] = recalculateBreakerLsig(mergedProduct);
        } else {
          updated[index] = mergedProduct;
        }
        setProducts(updated);
      } else {
        const updated = [...products];
        updated[index].hasHistory = false;
        updated[index].serviceHistory = [];
        updated[index].componentHistory = [];
        setProducts(updated);
      }
    } catch (error) {
      console.error("Failed to fetch serial history:", error);
    }
  };

  const handleSerialChangeDebounced = (index, serialNo) => {
    if (lookupTimers.current[index]) {
      clearTimeout(lookupTimers.current[index]);
    }

    lookupTimers.current[index] = setTimeout(() => {
      fetchProductHistory(serialNo, index);
    }, 600);
  };

  const handleProductHistoryTabChange = (productIndex, tab) => {
    const updated = [...products];
    updated[productIndex].activeHistoryTab = tab;
    setProducts(updated);
  };

  const addComponentToBeReplaced = (productIndex) => {
    const updated = [...products];
    const newComponent = {
      name: "",
      status: "To Be Replaced",
      identifiedDate: new Date().toISOString().slice(0, 10),
      remarks: ""
    };
    updated[productIndex].componentsToBeReplaced = [
      ...(updated[productIndex].componentsToBeReplaced || []),
      newComponent
    ];
    updated[productIndex].compReplace = updated[productIndex].componentsToBeReplaced.map(c => c.name);
    setProducts(updated);
  };

  const updateComponentToBeReplaced = (productIndex, componentIndex, field, value) => {
    const updated = [...products];
    const comps = [...(updated[productIndex].componentsToBeReplaced || [])];
    
    if (field === "status" && value === "Replaced") {
      const name = comps[componentIndex].name || "";
      const remarks = comps[componentIndex].remarks || "";
      
      comps.splice(componentIndex, 1);
      updated[productIndex].componentsToBeReplaced = comps;
      
      const newReplacement = {
        name,
        previousPart: "",
        newPart: "",
        quantity: 1,
        replacementDate: new Date().toISOString().slice(0, 10),
        remarks: remarks || "Replaced during this visit"
      };
      updated[productIndex].componentsReplaced = [
        ...(updated[productIndex].componentsReplaced || []),
        newReplacement
      ];
    } else {
      comps[componentIndex] = {
        ...comps[componentIndex],
        [field]: value
      };
      updated[productIndex].componentsToBeReplaced = comps;
    }

    updated[productIndex].compReplace = (updated[productIndex].componentsToBeReplaced || []).map(c => c.name);
    updated[productIndex].newComp = (updated[productIndex].componentsReplaced || []).map(c => c.name);
    setProducts(updated);
  };

  const removeComponentToBeReplaced = (productIndex, componentIndex) => {
    const updated = [...products];
    const comps = (updated[productIndex].componentsToBeReplaced || []).filter((_, idx) => idx !== componentIndex);
    updated[productIndex].componentsToBeReplaced = comps;
    updated[productIndex].compReplace = comps.map(c => c.name);
    setProducts(updated);
  };

  const addComponentReplaced = (productIndex) => {
    const updated = [...products];
    const newComponent = {
      name: "",
      previousPart: "",
      newPart: "",
      quantity: 1,
      replacementDate: new Date().toISOString().slice(0, 10),
      remarks: ""
    };
    updated[productIndex].componentsReplaced = [
      ...(updated[productIndex].componentsReplaced || []),
      newComponent
    ];
    updated[productIndex].newComp = updated[productIndex].componentsReplaced.map(c => c.name);
    setProducts(updated);
  };

  const updateComponentReplaced = (productIndex, componentIndex, field, value) => {
    const updated = [...products];
    const comps = [...(updated[productIndex].componentsReplaced || [])];
    comps[componentIndex] = {
      ...comps[componentIndex],
      [field]: value
    };
    updated[productIndex].componentsReplaced = comps;
    updated[productIndex].newComp = comps.map(c => c.name);
    setProducts(updated);
  };

  const removeComponentReplaced = (productIndex, componentIndex) => {
    const updated = [...products];
    const comps = (updated[productIndex].componentsReplaced || []).filter((_, idx) => idx !== componentIndex);
    updated[productIndex].componentsReplaced = comps;
    updated[productIndex].newComp = comps.map(c => c.name);
    setProducts(updated);
  };


  const mergeAndPersistComponentSuggestions = useCallback((items = []) => {
    const nextSuggestions = normalizeSuggestionList([
      ...componentSuggestionOptions,
      ...items
    ]);
    setComponentSuggestionOptions(nextSuggestions);
    localStorage.setItem(
      COMPONENT_SUGGESTIONS_STORAGE_KEY,
      JSON.stringify(nextSuggestions)
    );
  }, [componentSuggestionOptions]);
  const handleLSIGChange = (pIndex, lIndex, field, value) => {
    const updated = [...products];
    const currentRow = updated[pIndex].lsig[lIndex] || {};
    const isIRow = currentRow.type === "I" || lIndex === 2;
    const rating = updated[pIndex].rating;

    if (field === "setting" && isOffValue(currentRow.setting)) {
      const defaultRow = defaultLsigRows()[lIndex] || {};
      const isNowOff = isOffValue(value);
      const isSettingEmpty = !String(value || "").trim();
      const defaultTripCurrent = isNowOff
        ? "OFF"
        : formatTripCurrent(calculateTripCurrentValue(currentRow.type || defaultRow.type, value, rating));

      updated[pIndex].lsig[lIndex] = {
        ...currentRow,
        setting: value,
        seconds: isNowOff ? "OFF" : defaultRow.seconds || "",
        tripCurrent: defaultTripCurrent,
        current: isNowOff ? "OFF" : "",
        measured: isNowOff
          ? "OFF"
          : isSettingEmpty
            ? ""
            : isIRow
              ? calculateAutoMeasuredValue("", "I")
              : calculateAutoMeasuredValue(defaultRow.seconds, currentRow.type || defaultRow.type, defaultTripCurrent, value, rating),
        status: isNowOff ? "OFF" : defaultRow.status || "OK"
      };

      if (isNowOff) {
        setProducts(updated);
        return;
      }

      updated[pIndex] = recalculateBreakerLsig(updated[pIndex]);
      setProducts(updated);
      return;
    }

    if (field !== "setting" && isOffValue(currentRow.setting)) {
      updated[pIndex].lsig[lIndex] = {
        ...currentRow,
        seconds: "OFF",
        tripCurrent: "OFF",
        current: "OFF",
        measured: "OFF",
        status: "OFF"
      };
      setProducts(updated);
      return;
    }

    if (field === "setting" && isOffValue(value)) {
      updated[pIndex].lsig[lIndex] = {
        ...updated[pIndex].lsig[lIndex],
        setting: "OFF",
        seconds: "OFF",
        tripCurrent: "OFF",
        current: "OFF",
        measured: "OFF",
        status: "OFF"
      };
      setProducts(updated);
      return;
    }

    // Assign the updated value directly
    updated[pIndex].lsig[lIndex][field] = value;

    if (field === "tripCurrent") {
      // User entered custom Fault Current -> Auto-generate corresponding measured value
      if (!isOffValue(value)) {
        updated[pIndex].lsig[lIndex].measured = isIRow
          ? calculateAutoMeasuredValue("", "I")
          : calculateAutoMeasuredValue(currentRow.seconds, currentRow.type, value, currentRow.setting, rating);
      }
      updated[pIndex] = recalculateBreakerLsig(updated[pIndex], true);
      setProducts(updated);
      return;
    }

    if (field === "seconds") {
      // User edited Time (s) -> Auto-generate measured value based on current Fault Current
      updated[pIndex].lsig[lIndex].measured = calculateAutoMeasuredValue(
        value,
        currentRow.type,
        currentRow.tripCurrent,
        currentRow.setting,
        rating
      );
    }

    if (field === "setting") {
      // User edited Setting -> Primary behavior: calculate standard primary fault current and update measured
      const defaultTripVal = calculateTripCurrentValue(currentRow.type, value, rating);
      const formattedTrip = formatTripCurrent(defaultTripVal);
      updated[pIndex].lsig[lIndex].tripCurrent = formattedTrip;

      const trimmedVal = String(value || "").trim();
      if (!trimmedVal) {
        updated[pIndex].lsig[lIndex].measured = "";
      } else if (isIRow) {
        updated[pIndex].lsig[lIndex].measured = calculateAutoMeasuredValue("", "I");
      } else {
        updated[pIndex].lsig[lIndex].measured = calculateAutoMeasuredValue(
          currentRow.seconds,
          currentRow.type,
          formattedTrip,
          value,
          rating
        );
      }
    }

    updated[pIndex] = recalculateBreakerLsig(updated[pIndex], field !== "setting");
    setProducts(updated);
  };

  const getSignatureValue = async (ref, existingValue) => {
    return await extractLandscapeSignature(ref, existingValue);
  };

  const buildReportPayload = async () => {
    const customerSignature = await getSignatureValue(
      customerSignRef,
      existingCustomerSignature
    );
    const engineerSignature = await getSignatureValue(
      engineerSignRef,
      existingEngineerSignature
    );
    const complaintIdForSave =
      selectedComplaintId ||
      form.complaintRefId ||
      id;
    const consolidatedReportId = form.reportId || reportId || generateGeneralReportId();
    const preparedProducts = products.map((product) => ({
      ...product,
      serialNo: typeof product.serialNo === "string" ? product.serialNo.trim() : (product.serialNo || ""),
      ...(product.category === "breaker"
        ? {
            productName: normalizeBreakerProductName(product.productName || product.product || ""),
            showLsigTable: Boolean(product.showLsigMeasuredValues) || Boolean(product.showLsigTable),
            showLsigMeasuredValues: Boolean(product.showLsigMeasuredValues)
          }
        : product.category === "abb_warranty"
        ? {
            product: product.product || product.productName || "",
            productName: product.product || product.productName || "",
            model: product.model || "",
            serialNo: product.serialNo || "",
            additionalInfo: product.additionalInfo || "",
            additionalBoxes: normalizeAdditionalBoxes(product.additionalBoxes),
            rating: product.frameRating || product.rating || ""
          }
        : {}),
      testCertificateNumber:
        product.testCertificateNumber ||
        product.certificateNumber ||
        product.certificate_number ||
        ""
    }));

    return {
      ...form,
      reportId: consolidatedReportId,
      visitDate: fromInputDate(form.visitDate),
      complaintId: complaintIdForSave,
      generalServiceId: generalService?._id || generalServiceId || "",
      generalServiceRefId: generalService?.serviceId || "",
      reportType: generalService?._id || generalServiceId
        ? (isAdminMode ? "merged_final" : "engineer")
        : "standard",
      reportOwnerEngineerId: !isAdminMode ? (localStorage.getItem("engineerId") || "") : "",
      reportOwnerEngineerName: !isAdminMode ? (engineerName || form.engineer || "") : "",
      reportOwnerEngineerEmail: !isAdminMode ? (localStorage.getItem("engineerEmail") || "") : "",
      assignedTeam: !isAdminMode
        ? (() => {
            if (generalService && Array.isArray(generalService.assignedEngineers)) {
              const assignment = generalService.assignedEngineers.find((ae) =>
                [ae.engineerId, ae.engineerName, ae.engineerEmail]
                  .filter(Boolean)
                  .some((v) => [localStorage.getItem("engineerId"), localStorage.getItem("engineerEmail"), engineerName]
                    .filter(Boolean)
                    .map((item) => String(item).trim().toLowerCase())
                    .includes(String(v).trim().toLowerCase()))
              );
              if (assignment) return assignment.team || "";
            }
            const targetId = selectedComplaintId || form.complaintRefId || id;
            const complaint = allComplaints.find(c => String(c._id) === String(targetId) || String(c.complaintId) === String(targetId));
            if (complaint && Array.isArray(complaint.assignedEngineers)) {
              const assignment = complaint.assignedEngineers.find((ae) =>
                [ae.engineerId, ae.engineerName, ae.engineerEmail]
                  .filter(Boolean)
                  .some((v) => [localStorage.getItem("engineerId"), localStorage.getItem("engineerEmail"), engineerName]
                    .filter(Boolean)
                    .map((item) => String(item).trim().toLowerCase())
                    .includes(String(v).trim().toLowerCase()))
              );
              if (assignment) return assignment.team || "";
            }
            return "";
          })()
        : "",
      mergedFromReportIds: isAdminMode && generalService ? mergeReportsParam.split(",").map((value) => value.trim()).filter(Boolean) : [],
      mergedFromEngineers: isAdminMode && generalService
        ? (Array.isArray(generalService.reports)
            ? generalService.reports
                .filter((report) => mergeReportsParam.split(",").map((value) => value.trim()).filter(Boolean).includes(String(report._id)))
                .map((report) => report.reportOwnerEngineerName || report.engineer)
                .filter(Boolean)
            : [])
        : [],
      requesterRole: isAdminMode ? "admin" : "engineer",
      requesterEngineerLookup: !isAdminMode ? engineerLookup : "",
      requesterName: isAdminMode ? (localStorage.getItem("adminName") || "Admin") : engineerName,
      company: form.customerName,
      engineer: form.engineer || engineerName,
      approvalStatus: isAdminMode ? approvalStatus : "Approval Pending",
      engineerReportStatus: isAdminMode
        ? (approvalStatus === "Approved" ? "Approved" : "Submitted")
        : "Submitted",
      reviewRemarks: isAdminMode ? reviewRemarks : "",
      products: preparedProducts,
      customerSignature,
      engineerSignature,
      mergedBy: isAdminMode ? (localStorage.getItem("adminName") || "Admin") : ""
    };
  };

  const submitReport = async () => {
    try {
      const linkedTravel =
        travelByComplaintId[String(selectedComplaintId)] ||
        travelByComplaintId[String(form.complaintRefId)] ||
        travelByComplaintId[String(id)];

      if (!isAdminMode && !generalServiceId && !reportDocId && !canCreateReportFromTravel(linkedTravel)) {
        alert("Report creation is available only after travel status is updated to At Site.");
        return;
      }

      if (!isAdminMode && (approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked") && !editAccessGranted) {
        alert("This report has already been approved. You can only view or download the PDF.");
        return;
      }

      if (!isAdminMode && generalService?.mergedReport) {
        alert("This general service has already been merged into a final report. Engineer reports cannot be edited now.");
        return;
      }

      setSaving(true);
      const token = localStorage.getItem("token");
      const payload = await buildReportPayload();
      const consolidatedReportId = payload.reportId;

      // Server regenerates the proper multi-page Puppeteer PDF automatically on save.
      // Sending the report data directly — no client-side PDF generation needed.
      const response = reportDocId
        ? await axios.put(
            API_BASE_URL + `/api/visits/${reportDocId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        : await axios.post(
            API_BASE_URL + "/api/visits",
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

      setReportDocId(response.data._id || "");
      setReportId(response.data.reportId || consolidatedReportId);
      setApprovalStatus(response.data.approvalStatus || "Approval Pending");
      setReviewRemarks(response.data.reviewRemarks || "");
      setForm((current) => ({ ...current, reportId: response.data.reportId || consolidatedReportId }));
      setExistingCustomerSignature(payload.customerSignature);
      setExistingEngineerSignature(payload.engineerSignature);
      mergeAndPersistComponentSuggestions(
        extractProductComponentSuggestions(payload.products)
      );
      skipLeaveProtectionRef.current = true;
      if (draftStorageKey) {
        localStorage.removeItem(draftStorageKey);
      }

      alert(
        reportDocId
          ? "Service Report Updated Successfully"
          : "Service Report Submitted Successfully"
      );
      navigate(isAdminMode ? `/reports/${response.data._id}` : "/my-reports");
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || err.message || "Error submitting report");
    } finally {
      setSaving(false);
    }
  };

  const deleteReport = async () => {
    if (!reportDocId) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete report ${reportId || form.reportId || ""}?`
    );

    if (!confirmed) return;

    try {
      setDeleting(true);
      const token = localStorage.getItem("token");
      await axios.delete(API_BASE_URL + `/api/visits/${reportDocId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      alert("Report deleted successfully");
      navigate(isAdminMode ? "/complaints" : "/my-reports");
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Failed to delete report");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    const loadingContent = <div className="p-6">Loading report form...</div>;
    return isAdminMode ? loadingContent : <EngineerLayout>{loadingContent}</EngineerLayout>;
  }

  const linkedTravel =
    travelByComplaintId[String(selectedComplaintId)] ||
    travelByComplaintId[String(form.complaintRefId)] ||
    travelByComplaintId[String(id)];
  const canCreateReportNow = isAdminMode || Boolean(generalServiceId) || reportDocId || canCreateReportFromTravel(linkedTravel);

  const content = (
    <>
      {generalService ? (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800">
          General Service: {generalService.serviceId || "-"}
          {generalService.serviceName ? ` | ${generalService.serviceName}` : ""}
          {generalService.status ? ` | ${generalService.status}` : ""}
        </div>
      ) : null}

      {!isAdminMode && reportDocId && (
        <div className={`mb-4 rounded-xl border px-4 py-3 text-sm font-medium ${
          (approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked")
            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
            : approvalStatus === "Edit Access Granted"
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : approvalStatus === "Changes Requested"
            ? "border-rose-200 bg-rose-50 text-rose-700"
            : "border-amber-200 bg-amber-50 text-amber-700"
        }`}>
          Report approval status: {approvalStatus}.
          {(approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked")
            ? " Editing is locked for engineers."
            : approvalStatus === "Edit Access Granted"
            ? " You have temporary edit access to make required changes."
            : approvalStatus === "Changes Requested"
            ? ` Admin remarks: ${reviewRemarks || "Please update the report and resubmit."}`
            : " The report is waiting for admin approval."}
        </div>
      )}

      {!isAdminMode && generalService?.mergedReport && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          Final merged report already exists for this service. Your engineer report can be viewed, but it cannot be changed after merge.
        </div>
      )}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Service Report</h1>
          {draftSaved && !reportDocId && (
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
              Draft Saved
            </p>
          )}
        </div>
        <div className="sm:text-right">
          <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Consolidated Report ID</p>
          <p className="text-lg font-mono font-bold text-blue-600">{reportId || form.reportId}</p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 shadow sm:p-6 lg:grid-cols-2 lg:gap-6">
        <div>
          <label className="block text-sm font-bold mb-1 text-blue-900">Assigned Complaint</label>
          <select
            className="border p-2 w-full rounded focus:ring-2 focus:ring-blue-300 outline-none"
            value={selectedComplaintId}
            onChange={(e) => {
              const complaint = allComplaints.find(
                (item) => item._id === e.target.value
              );
              if (complaint) {
                selectComplaint(complaint);
              }
            }}
          >
            <option value="">Select assigned complaint</option>
            {allComplaints.map((complaint) => (
              <option key={complaint._id} value={complaint._id}>
                {complaint.complaintId} - {complaint.company}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold mb-1 text-blue-900">Consolidated Report ID</label>
          <input
            value={reportId || form.reportId}
            readOnly
            className="border p-2 w-full rounded bg-white font-mono text-blue-700 focus:ring-2 focus:ring-blue-300 outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded bg-white p-4 shadow sm:p-6 md:grid-cols-2">
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Customer Name</label>
          <input name="customerName" value={form.customerName} className="w-full rounded border bg-gray-100 p-2" readOnly />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Address</label>
          <input name="address" value={form.address} className="w-full rounded border bg-gray-100 p-2" readOnly />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">City</label>
          <input name="city" value={form.city} className="w-full rounded border bg-gray-100 p-2" readOnly />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Engineer</label>
          <input
            name="engineer"
            value={form.engineer}
            onChange={handleChange}
            className={`w-full rounded border p-2 ${isAdminMode ? "" : "bg-gray-100"}`}
            readOnly={!isAdminMode}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Contact Name</label>
          <input name="contactName" value={form.contactName} onChange={handleChange} className="w-full rounded border p-2" />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Phone Number</label>
          <input name="phone" value={form.phone} onChange={handleChange} className="w-full rounded border p-2" />
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Product Type</label>
          <select value={productType} onChange={(e) => setProductType(e.target.value)} className="w-full rounded border p-2">
            <option value="">Select Product Type</option>
            {activeProductTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-sm font-bold text-gray-700 mb-1">Email Address</label>
          <input name="email" value={form.email} onChange={handleChange} className="w-full rounded border p-2" />
        </div>

        {isAbbWarrantyComplaint && (
          <div className="flex flex-col md:col-span-2">
            <label className="text-sm font-bold text-gray-700 mb-1 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base text-teal-600">verified</span>
                ABB Warranty Ticket Number
              </span>
              <span className="text-xs font-normal text-slate-500">(For ABB Warranty reports)</span>
            </label>
            <input
              name="abbTicketNo"
              value={form.abbTicketNo || ""}
              placeholder="e.g. ABB-TK-12345 / 1-12345678"
              onChange={(e) => {
                const val = e.target.value;
                setForm(prev => ({ ...prev, abbTicketNo: val }));
                setProducts(prev => prev.map(p => p.category === "abb_warranty" ? { ...p, abbTicketNo: val } : p));
              }}
              className="w-full rounded border p-2 text-sm font-medium focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none"
            />
          </div>
        )}

        <div className="md:col-span-2">
          <label className="font-bold block mb-1 text-gray-700 uppercase text-xs">Visit Date (DD-MM-YYYY)</label>
          <input
            type="date"
            name="visitDate"
            value={form.visitDate}
            className="border p-2 w-full rounded focus:ring-2 focus:ring-green-400"
            onChange={handleDateChange}
          />
          <p className="text-xs text-blue-600 mt-1 font-semibold italic">
            Selected Date: {fromInputDate(form.visitDate)}
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-bold text-gray-700 mb-1">Customer Complaint Details</label>
          <textarea
            name="complaint"
            value={form.complaint}
            placeholder="Details will autofill or type here"
            className="border p-2 w-full rounded h-24"
            onChange={handleChange}
          />
        </div>
      </div>

      {products.length > 0 && (
        <div className="mt-8 mb-2">
          <h2 className="text-xl font-bold text-gray-900">Products Serviced</h2>
          <p className="text-xs text-gray-500 mt-1">
            {products.length} product{products.length === 1 ? "" : "s"} recorded for this report.
          </p>
        </div>
      )}



      {products.map((p, i) => {
        const isExpanded = expandedProductIds.includes(p.id);

        return (
        <div
          key={p.id || i}
          ref={(node) => {
            if (node) {
              productCardRefs.current[p.id] = node;
            } else {
              delete productCardRefs.current[p.id];
            }
          }}
          className="mt-6 rounded border-t-8 border-green-600 bg-white p-4 shadow sm:p-6"
        >
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-green-800 flex items-center gap-2">
                  #{i + 1}
                  {p.sourceEngineer && (
                    <span className="inline-block bg-white text-green-800 text-xs px-2 py-0.5 rounded-full border border-green-200 font-bold">
                      {p.sourceEngineer}
                      {p.sourceTeam ? (() => {
                        const matchedTeam = dynamicTeams.find(t => t.id === p.sourceTeam || t.name === p.sourceTeam);
                        const tName = matchedTeam ? matchedTeam.name : (p.sourceTeam === "team_1" ? "Team 1" : p.sourceTeam === "team_2" ? "Team 2" : p.sourceTeam === "team_3" ? "Team 3" : p.sourceTeam);
                        return ` (${tName})`;
                      })() : ""}
                    </span>
                  )}
                </h2>
                <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-green-900 sm:grid-cols-2">
                  <p><span className="font-semibold">Feeder:</span> {p.feeder || "-"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdminMode && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      disabled={i === 0}
                      onClick={() => moveProduct(i, "up")}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                      title="Move Product Up"
                    >
                      <span className="material-symbols-outlined text-sm">arrow_upward</span>
                    </button>
                    <button
                      type="button"
                      disabled={i === products.length - 1}
                      onClick={() => moveProduct(i, "down")}
                      className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                      title="Move Product Down"
                    >
                      <span className="material-symbols-outlined text-sm">arrow_downward</span>
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleProductExpanded(p.id)}
                  className="inline-flex items-center justify-center rounded-xl border border-green-300 bg-white px-4 py-2 text-sm font-semibold text-green-800 transition hover:bg-green-100"
                >
                  {isExpanded ? "Hide Details" : "View More"}
                </button>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => deleteProduct(i)}
                className="inline-flex items-center justify-center rounded-xl bg-red-100 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-200"
              >
                Remove Product
              </button>
            </div>
          </div>

          {isExpanded && (
            <>
          {p.category === "breaker" && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-blue-900 mb-1 flex items-center gap-1">
                    Serial No <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={p.serialNo || ""}
                    placeholder="Type or select serial number"
                    className="w-full rounded border-2 border-blue-200 p-2 font-mono text-sm uppercase focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    list={`serial-suggestions-${i}`}
                    onChange={(e) => handleProductChange(i, "serialNo", e.target.value)}
                  />
                  <datalist id={`serial-suggestions-${i}`}>
                    {customerProducts.map((cp, cpIdx) => (
                      <option key={cpIdx} value={cp.product?.serialNo || ""}>
                        {cp.product?.productName || cp.product?.product || "Product"}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Product Name</label>
                  {p.productNameMode === "others" || (!p.productNameMode && getBreakerProductNameOptionValue(p.productName) === "others") ? (
                    <input
                      value={p.productName || ""}
                      placeholder="Type product name"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"productName",e.target.value)}
                    />
                  ) : (
                    <select
                      value={p.productNameMode || getBreakerProductNameOptionValue(p.productName)}
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"productNameOption",e.target.value)}
                    >
                      <option value="">Select Product Name</option>
                      {breakerProductNameOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {(p.productNameMode === "others" || (!p.productNameMode && getBreakerProductNameOptionValue(p.productName) === "others")) && (
                    <button
                      type="button"
                      onClick={() => handleProductChange(i, "productNameOption", "")}
                      className="mt-2 self-start rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      Choose from list
                    </button>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Make</label>
                  {getMakeOptions(p.productName).length > 0 ? (
                    <select
                      value={getMakeOptions(p.productName).includes(p.make) ? p.make : (p.makeOther ? "Other" : "")}
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"make",e.target.value)}
                    >
                      <option value="">Select Make</option>
                      {getMakeOptions(p.productName).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={p.make || ""}
                      placeholder="e.g. ABB"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"make",e.target.value)}
                    />
                  )}
                </div>

                {getMakeOptions(p.productName).length > 0 && (!getMakeOptions(p.productName).includes(p.make) || p.makeOther) && (
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Other Make</label>
                    <input
                      value={p.makeOther || ""}
                      placeholder="Enter make"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"makeOther",e.target.value)}
                    />
                  </div>
                )}



                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Rating (Amps)</label>
                  <input value={p.rating || ""} placeholder="Rating (Amps)" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"rating",e.target.value)} />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex min-w-0 flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">No of Poles</label>
                    <input value={p.pole || ""} placeholder="No of Poles" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"pole",e.target.value)} />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Neutral</label>
                    <select value={p.neutral || ""} className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"neutral",e.target.value)}>
                      <option value="">Select</option>
                      <option value="-">-</option>
                      <option value="50%">50%</option>
                      <option value="100%">100%</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Feeder</label>
                  <input value={p.feeder || ""} placeholder="Feeder" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"feeder",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Panel Area</label>
                  <input
                    value={p.panelArea || ""}
                    placeholder="Panel Area"
                    className="w-full rounded border p-2"
                    list={`panel-area-suggestions-${i}`}
                    onChange={(e)=>handleProductChange(i,"panelArea",e.target.value)}
                  />
                  <datalist id={`panel-area-suggestions-${i}`}>
                    {panelAreaSuggestions.map((area, areaIdx) => (
                      <option key={areaIdx} value={area} />
                    ))}
                  </datalist>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Breaker Type</label>
                  <select
                    value={getBreakerTypeOptions(p.productName).includes(p.breakerType) ? p.breakerType : (p.breakerTypeOther ? "Other" : "")}
                    className="w-full rounded border p-2"
                    onChange={(e)=>handleProductChange(i,"breakerType",e.target.value)}
                  >
                    <option value="">Select Type</option>
                    {getBreakerTypeOptions(p.productName).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {getBreakerSubTypeOptions(p.breakerType).length > 0 ? (
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Sub Type / Trip Unit</label>
                    <select
                      value={getBreakerSubTypeOptions(p.breakerType).includes(p.subType) ? p.subType : (p.subTypeOther ? "Other" : "")}
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"subType",e.target.value)}
                    >
                      <option value="">Select Sub Type</option>
                      {getBreakerSubTypeOptions(p.breakerType).map((option) => (
                        <option key={`${p.id || i}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Sub Type / Trip Unit</label>
                    <input
                      value={p.subType || ""}
                      placeholder="Sub Type"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"subType",e.target.value)}
                    />
                  </div>
                )}

                {(!getBreakerTypeOptions(p.productName).includes(p.breakerType) || p.breakerTypeOther) && (
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Other Breaker Type</label>
                    <input
                      value={p.breakerTypeOther || ""}
                      placeholder="Enter breaker type"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"breakerTypeOther",e.target.value)}
                    />
                  </div>
                )}

                {getBreakerSubTypeOptions(p.breakerType).length > 0 &&
                  ((!getBreakerSubTypeOptions(p.breakerType).includes(p.subType) && Boolean(p.subType)) || p.subTypeOther) && (
                    <div className="flex flex-col">
                      <label className="text-xs font-bold uppercase text-gray-500 mb-1">Other Sub Type</label>
                      <input
                        value={p.subTypeOther || ""}
                        placeholder="Enter sub type"
                        className="w-full rounded border p-2"
                        onChange={(e)=>handleProductChange(i,"subTypeOther",e.target.value)}
                      />
                    </div>
                  )}

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Frame Size</label>
                  <select
                    value={getBreakerFrameOptions(p.breakerType).includes(p.frame) ? p.frame : (p.frameOther ? "Other" : "")}
                    className="w-full rounded border p-2"
                    onChange={(e)=>handleProductChange(i,"frame",e.target.value)}
                  >
                    <option value="">Select Frame</option>
                    {getBreakerFrameOptions(p.breakerType).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {(!getBreakerFrameOptions(p.breakerType).includes(p.frame) || p.frameOther) && (
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-gray-500 mb-1">Other Frame</label>
                    <input
                      value={p.frameOther || ""}
                      placeholder="Enter frame"
                      className="w-full rounded border p-2"
                      onChange={(e)=>handleProductChange(i,"frameOther",e.target.value)}
                    />
                  </div>
                )}

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Release S.No Number</label>
                  <input
                    value={p.releaseSerialNumber || ""}
                    placeholder="Release S.No Number"
                    className="w-full rounded border p-2"
                    onChange={(e)=>handleProductChange(i,"releaseSerialNumber",e.target.value)}
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-gray-500 mb-1">Warranty Status</label>
                  <select value={p.warranty || ""} className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"warranty",e.target.value)}>
                    <option value="">Select Warranty</option>
                    <option>In Warranty</option>
                    <option>Out of Warranty</option>
                  </select>
                </div>
              </div>

              {p.hasHistory && (
                <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50/50 p-4">
                  <h3 className="text-xs font-bold text-blue-900 mb-3 flex items-center gap-1.5 uppercase tracking-wide">
                    <span className="material-symbols-outlined text-lg">history</span>
                    Asset Service & Component History (Serial: {p.serialNo})
                  </h3>
                  
                  <div className="flex border-b border-blue-200 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "service")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "service" || !p.activeHistoryTab
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Service History ({p.serviceHistory?.length || 0})
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "components")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "components"
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Component History ({p.componentHistory?.length || 0})
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "prev-test")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "prev-test"
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Previous Test Reference
                    </button>
                  </div>

                  {(p.activeHistoryTab === "service" || !p.activeHistoryTab) && (
                    <div className="space-y-3">
                      {(!p.serviceHistory || p.serviceHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No previous service visits found.</p>
                      ) : (
                        <div className="divide-y divide-blue-100 max-h-[250px] overflow-y-auto pr-1">
                          {p.serviceHistory.map((h, idx) => (
                            <div key={idx} className="py-2.5 first:pt-0 last:pb-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-bold text-blue-900">{h.reportId} ({fromInputDate(h.visitDate)})</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  String(h.serviceStatus).toLowerCase().includes("ok") || String(h.serviceStatus).toLowerCase().includes("satisfactorily")
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-rose-100 text-rose-700"
                                }`}>{h.serviceStatus}</span>
                              </div>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Engineer:</span> {h.engineer}</p>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Observation:</span> {h.observation}</p>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Action:</span> {h.actionTaken}</p>
                              {h.remarks && <p className="text-xs text-slate-500 italic mt-0.5">Remarks: "{h.remarks}"</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {p.activeHistoryTab === "components" && (
                    <div>
                      {(!p.componentHistory || p.componentHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No component replacement records found.</p>
                      ) : (
                        <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-blue-100/50 text-blue-950 sticky top-0">
                              <tr>
                                <th className="px-2 py-1.5 font-bold">Component</th>
                                <th className="px-2 py-1.5 font-bold">Status</th>
                                <th className="px-2 py-1.5 font-bold text-center">Identified Date</th>
                                <th className="px-2 py-1.5 font-bold text-center">Replaced Date</th>
                                <th className="px-2 py-1.5 font-bold">Visit</th>
                                <th className="px-2 py-1.5 font-bold">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100 bg-white">
                              {p.componentHistory.map((c, idx) => (
                                <tr key={idx} className="hover:bg-blue-100/10">
                                  <td className="px-2 py-2 font-semibold text-blue-900">{c.name}</td>
                                  <td className="px-2 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      c.status === "Replaced"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : c.status === "To Be Replaced" || c.status === "Pending"
                                        ? "bg-rose-100 text-rose-700"
                                        : "bg-slate-100 text-slate-700"
                                    }`}>{c.status}</span>
                                  </td>
                                  <td className="px-2 py-2 text-center text-slate-600">{fromInputDate(c.identifiedDate) || "-"}</td>
                                  <td className="px-2 py-2 text-center text-slate-600">{fromInputDate(c.replacedDate) || "-"}</td>
                                  <td className="px-2 py-2 font-mono text-[10px] text-blue-800">{c.reportId}</td>
                                  <td className="px-2 py-2 text-slate-500 max-w-[150px] truncate" title={`${c.engineer}: ${c.remarks}`}>
                                    <span className="font-semibold text-slate-700">{c.engineer}:</span> {c.remarks || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {p.activeHistoryTab === "prev-test" && (
                    <div className="space-y-3 text-xs">
                      {(!p.serviceHistory || p.serviceHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No previous test data reference found.</p>
                      ) : (() => {
                        const latestTest = p.serviceHistory[p.serviceHistory.length - 1];
                        const t = latestTest.testData || {};
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-bold text-blue-900 mb-1.5 border-b border-blue-100 pb-0.5">Coil Measurements (Latest)</h4>
                              <ul className="space-y-1 text-slate-600">
                                <li><span className="font-semibold text-slate-700">Motor Voltage/Status:</span> {t.motorVoltages?.join(", ") || "-"} ({t.motorStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">Shunt (YO) Voltage/Status:</span> {t.yoVoltages?.join(", ") || "-"} ({t.yoStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">Closing (YC) Voltage/Status:</span> {t.ycVoltages?.join(", ") || "-"} ({t.ycStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">UV Release Voltage/Status:</span> {t.uvVoltages?.join(", ") || "-"} ({t.uvStatus || "-"})</li>
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-bold text-blue-900 mb-1.5 border-b border-blue-100 pb-0.5">LSIG Settings (Latest)</h4>
                              {(!t.lsig || t.lsig.length === 0) ? (
                                <p className="text-slate-500 italic">No LSIG parameters recorded.</p>
                              ) : (
                                <ul className="space-y-1 text-slate-600">
                                  {t.lsig.map((row, rIdx) => (
                                    <li key={rIdx}>
                                      <span className="font-semibold text-slate-700">{row.parameter || row.param || "-"}:</span> {row.setting || "-"} (Measured: {row.measured || "-"}, Result: {row.result || "-"})
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4">
                <label className="text-xs font-bold uppercase text-gray-500 mb-1 block">Additional Info</label>
                <textarea
                  value={p.additionalInfo || ""}
                  placeholder="Additional breaker information..."
                  className="border p-2 rounded w-full h-20"
                  onChange={(e)=>handleProductChange(i,"additionalInfo",e.target.value)}
                />
              </div>

              {renderAdditionalBoxes(p, i)}

              <div className="mt-6">
                <label className="font-bold text-sm block mb-2 text-gray-700">Technical Checks / Accessories</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-gray-50 p-3 rounded border border-gray-200 shadow-sm">
                  {accessoryOptions.map((accessory) => (
                    <div
                      key={accessory.label}
                      className="rounded border border-gray-200 bg-white p-3"
                    >
                      <label className="flex items-center gap-2 cursor-pointer hover:text-green-700 font-medium">
                        <input
                          type="checkbox"
                          checked={(p.checks || []).includes(accessory.label)}
                          onChange={() => handleCheckbox(i, accessory.label)}
                        />
                        {accessory.label}
                      </label>

                      {(p.checks || []).includes(accessory.label) && (
                        <div className="mt-3 pl-6">
                          <p className="text-xs font-semibold text-blue-900 mb-2">
                            Current type
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {["AC", "DC"].map((typeOption) => (
                              <label
                                key={`${accessory.label}-${typeOption}`}
                                className="flex items-center gap-2 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={p[accessory.typeField] === typeOption}
                                  onChange={() =>
                                    handleAccessoryCurrentTypeChange(
                                      i,
                                      accessory.typeField,
                                      typeOption
                                    )
                                  }
                                />
                                {typeOption}
                              </label>
                            ))}
                          </div>
                          <p className="text-xs font-semibold text-blue-900 mb-2">
                            Choose voltage
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {accessory.options.map((option) => (
                              <label
                                key={`${accessory.label}-${option}`}
                                className="flex items-center gap-2 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={(p[accessory.field] || []).includes(option)}
                                  onChange={() =>
                                    handleVoltageCheckbox(i, accessory.field, option)
                                  }
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Select only one voltage.
                          </p>
                          <div className="mt-4">
                            <label className="text-xs font-semibold text-blue-900 mb-1 block">
                              Result
                            </label>
                            <div className="flex flex-wrap gap-4 rounded border border-gray-200 bg-white p-3">
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={p[accessory.statusField] === "OK"}
                                  onChange={() => {
                                    const hasVoltage = (p[accessory.field] || []).length > 0;
                                    if (hasVoltage) {
                                      handleProductChange(i, accessory.statusField, "OK");
                                    } else {
                                      handleProductChange(
                                        i,
                                        accessory.statusField,
                                        p[accessory.statusField] === "OK" ? "" : "OK"
                                      );
                                    }
                                  }}
                                />
                                OK
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={p[accessory.statusField] === "NOT OK"}
                                  onChange={(e) => {
                                    const hasVoltage = (p[accessory.field] || []).length > 0;
                                    if (hasVoltage) {
                                      handleProductChange(
                                        i,
                                        accessory.statusField,
                                        e.target.checked ? "NOT OK" : "OK"
                                      );
                                    } else {
                                      handleProductChange(
                                        i,
                                        accessory.statusField,
                                        e.target.checked ? "NOT OK" : ""
                                      );
                                    }
                                  }}
                                />
                                NOT OK
                              </label>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Leave both unchecked if the accessory is not present.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 overflow-x-auto">
                <div className="mb-3 space-y-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <label className="flex items-start gap-2 font-semibold">
                      <input
                        type="checkbox"
                        checked={Boolean(p.showLsigTable)}
                        onChange={(e) => handleProductChange(i, "showLsigTable", e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-blue-300"
                      />
                      <span>Show LSIG table on generated report PDF</span>
                    </label>
                    <label className="flex items-start gap-2 font-semibold">
                      <input
                        type="checkbox"
                        checked={Boolean(p.showLsigMeasuredValues)}
                        onChange={(e) => handleProductChange(i, "showLsigMeasuredValues", e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-blue-300"
                      />
                      <span>Include Measured and Status columns on PDF</span>
                    </label>
                  </div>
                  <p className="text-xs leading-5 text-blue-700">
                    The LSIG table appears on the generated PDF only when the first box is checked. If Measured and Status is checked, the LSIG table is enabled automatically and those two columns are included.
                  </p>
                </div>
                <label className="font-bold text-sm block mb-2 text-gray-700">LSIG Settings & Measured Values</label>
                <table className="w-full border border-collapse text-sm">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="p-2 border">Sl</th>
                      <th className="p-2 border">Function</th>
                      <th className="p-2 border">Setting</th>
                      <th className="p-2 border">Time (s)</th>
                      <th className="p-2 border">Current (A)</th>
                      <th className="p-2 border">Curve</th>
                      <th className="p-2 border">Fault Current (A)</th>
                      <th className="p-2 border">Measured</th>
                      <th className="p-2 border">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.lsig.map((row,l)=>(
                      <tr key={l} className="text-center border-t hover:bg-gray-50 transition">
                        <td className="p-2 border">{l+1}</td>
                        <td className="p-2 border font-bold text-blue-800">{row.type} ({row.func})</td>
                        <td className="p-2 border">
                          <input
                            value={row.setting || ""}
                            className="border p-1 w-full text-center rounded focus:ring-1 focus:ring-blue-500"
                            onChange={(e) => handleLSIGChange(i, l, "setting", e.target.value)}
                          />
                        </td>
                        <td className="p-2 border">
                          {row.type !== "I" || isOffValue(row.setting) ? (
                            <input
                              value={row.seconds || ""}
                              disabled={isOffValue(row.setting)}
                              className={`border p-1 w-full text-center rounded focus:ring-1 focus:ring-blue-500 ${isOffValue(row.setting) ? "bg-gray-200 font-semibold text-gray-500" : ""}`}
                              onChange={(e) => handleLSIGChange(i, l, "seconds", e.target.value)}
                            />
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="p-2 border">
                          <input
                            value={row.current || ""}
                            readOnly
                            className="border p-1 bg-gray-200 w-full text-center rounded font-semibold text-gray-800"
                          />
                        </td>
                        <td className="p-2 border font-semibold text-gray-700">
                          {row.formula || ""}
                        </td>
                        <td className="p-2 border">
                          <input
                            value={row.tripCurrent || ""}
                            disabled={isOffValue(row.setting)}
                            placeholder={isOffValue(row.setting) ? "OFF" : "Fault Current (A)"}
                            className={`border p-1 w-full text-center rounded font-bold text-slate-800 focus:ring-1 focus:ring-blue-500 ${isOffValue(row.setting) ? "bg-gray-200 font-semibold text-gray-500" : "bg-white hover:border-blue-400 focus:bg-blue-50/30"}`}
                            onChange={(e) => handleLSIGChange(i, l, "tripCurrent", e.target.value)}
                          />
                        </td>
                        <td className="p-2 border">
                          <input
                            value={row.measured || ""}
                            disabled={isOffValue(row.setting)}
                            placeholder={isOffValue(row.setting) ? "OFF" : "e.g. 11769 ms"}
                            className={`border p-1 w-full text-center rounded font-medium focus:ring-1 focus:ring-blue-500 ${isOffValue(row.setting) ? "bg-gray-200 font-semibold text-gray-500" : "bg-white hover:border-blue-400 focus:bg-blue-50/30"}`}
                            onChange={(e) => handleLSIGChange(i, l, "measured", e.target.value)}
                          />
                        </td>
                        <td className="p-2 border">
                          <select
                            value={row.status || ""}
                            disabled={isOffValue(row.setting)}
                            className={`border p-1 w-full rounded focus:ring-1 focus:ring-blue-500 ${isOffValue(row.setting) ? "bg-gray-200 font-semibold text-gray-500" : ""}`}
                            onChange={(e) => handleLSIGChange(i, l, "status", e.target.value)}
                          >
                            <option>OK</option>
                            <option>NOT OK</option>
                            <option>OFF</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-6">
                <div className="flex flex-col">
                  <label className="text-sm font-bold text-gray-700 mb-1">Engineer Remarks</label>
                  <textarea value={p.remarks || ""} placeholder="General remarks..." className="border p-2 w-full rounded" onChange={(e)=>handleProductChange(i,"remarks",e.target.value)} />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                    <span className="material-symbols-outlined text-lg text-rose-500">warning_amber</span>
                    Current Defective Components (To Be Replaced)
                  </label>
                  <div className="space-y-3">
                    {(p.componentsToBeReplaced || []).length === 0 ? (
                      <p className="text-xs text-slate-500 italic pl-1">No defective components identified for replacement.</p>
                    ) : (
                      (p.componentsToBeReplaced || []).map((comp, compIdx) => (
                        <div key={`${compIdx}-replace`} className="p-3 bg-rose-50/50 border border-rose-100 rounded-xl space-y-2.5">
                          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-4">
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Component Name</label>
                              <input
                                value={comp.name || ""}
                                placeholder="Component name"
                                className="border p-1.5 text-xs rounded bg-white"
                                list="component-suggestions"
                                onChange={(e) => updateComponentToBeReplaced(i, compIdx, "name", e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Status</label>
                              <select
                                value={comp.status || "To Be Replaced"}
                                className="border p-1.5 text-xs rounded bg-white font-semibold text-rose-700"
                                onChange={(e) => updateComponentToBeReplaced(i, compIdx, "status", e.target.value)}
                              >
                                <option value="To Be Replaced">To Be Replaced</option>
                                <option value="Pending">Pending</option>
                                <option value="Replaced">Replaced (Move to Replaced Section)</option>
                                <option value="OK / No Replacement Required">OK / No Replacement Required</option>
                                <option value="Removed / No Longer Required">Removed / No Longer Required</option>
                              </select>
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Identified Date</label>
                              <input
                                value={fromInputDate(comp.identifiedDate) || ""}
                                className="border p-1.5 text-xs rounded bg-slate-50 text-slate-500"
                                readOnly
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Remarks</label>
                              <input
                                value={comp.remarks || ""}
                                placeholder="Remarks..."
                                className="border p-1.5 text-xs rounded bg-white"
                                onChange={(e) => updateComponentToBeReplaced(i, compIdx, "remarks", e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeComponentToBeReplaced(i, compIdx)}
                              className="px-2.5 py-1 text-xs rounded bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                    <button
                      type="button"
                      onClick={() => addComponentToBeReplaced(i)}
                      className="self-start px-4 py-2 rounded bg-rose-100 hover:bg-rose-200 text-rose-700 font-semibold text-xs transition"
                    >
                      + Add Defective Component
                    </button>
                  </div>
                </div>

                <div className="flex flex-col mt-6 border-t border-slate-100 pt-6">
                  <label className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                    <span className="material-symbols-outlined text-lg text-emerald-500">check_circle</span>
                    Components Replaced During This Visit
                  </label>
                  <div className="space-y-3">
                    {(p.componentsReplaced || []).length === 0 ? (
                      <p className="text-xs text-slate-500 italic pl-1">No components replaced during this visit yet.</p>
                    ) : (
                      (p.componentsReplaced || []).map((comp, compIdx) => (
                        <div key={`${compIdx}-new`} className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl space-y-2">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Component Name</label>
                              <input
                                value={comp.name || ""}
                                placeholder="Component name"
                                className="border p-1.5 text-xs rounded bg-white font-semibold"
                                list="component-suggestions"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "name", e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Previous Part / Serial</label>
                              <input
                                value={comp.previousPart || ""}
                                placeholder="Old part details"
                                className="border p-1.5 text-xs rounded bg-white"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "previousPart", e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">New Part / Serial</label>
                              <input
                                value={comp.newPart || ""}
                                placeholder="New part details"
                                className="border p-1.5 text-xs rounded bg-white"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "newPart", e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Quantity</label>
                              <input
                                type="number"
                                value={comp.quantity || 1}
                                className="border p-1.5 text-xs rounded bg-white font-bold"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "quantity", Number(e.target.value))}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div className="flex flex-col">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Replacement Date</label>
                              <input
                                type="date"
                                value={comp.replacementDate || ""}
                                className="border p-1.5 text-xs rounded bg-white"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "replacementDate", e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col sm:col-span-2">
                              <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Remarks / Cost / Spare Info</label>
                              <input
                                value={comp.remarks || ""}
                                placeholder="Cost details, warranty or general remarks"
                                className="border p-1.5 text-xs rounded bg-white"
                                onChange={(e) => updateComponentReplaced(i, compIdx, "remarks", e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeComponentReplaced(i, compIdx)}
                              className="px-2.5 py-1 text-xs rounded bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                    <button
                      type="button"
                      onClick={() => addComponentReplaced(i)}
                      className="self-start px-4 py-2 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold text-xs transition"
                    >
                      + Add Replaced Component
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-gray-700 mb-1">Status After Service</label>
                  <select 
                    value={
                      ["Working satisfactorily & Release found Okay", "Operating Unit Failure, Not Adviced to Use", "Release Failure, Not Adviced to Use", "Trip Unit Failure, Not Adviced to Use", "CT is deteriorated. it may have a nuisance trip", "Mechanism Deteriorated, Not Adviced to Use"].includes(p.serviceStatus) 
                        ? p.serviceStatus 
                        : (p.serviceStatus ? "Other" : "")
                    } 
                    className="border p-2 w-full rounded" 
                    onChange={(e)=>{
                      if (e.target.value === "Other") {
                        handleProductChange(i, "serviceStatus", " ");
                      } else {
                        handleProductChange(i, "serviceStatus", e.target.value);
                      }
                    }}
                  >
                    <option value="">Select Status</option>
                    <option>Working satisfactorily & Release found Okay</option>
                    <option>Operating Unit Failure, Not Adviced to Use</option>
                    <option>Release Failure, Not Adviced to Use</option>
                    <option>Trip Unit Failure, Not Adviced to Use</option>
                    <option>CT is deteriorated. it may have a nuisance trip</option>
                    <option>Mechanism Deteriorated, Not Adviced to Use</option>
                    <option value="Other">Other</option>
                  </select>
                  {p.serviceStatus && !["Working satisfactorily & Release found Okay", "Operating Unit Failure, Not Adviced to Use", "Release Failure, Not Adviced to Use", "Trip Unit Failure, Not Adviced to Use", "CT is deteriorated. it may have a nuisance trip", "Mechanism Deteriorated, Not Adviced to Use"].includes(p.serviceStatus) ? (
                    <input
                      type="text"
                      placeholder="Please specify"
                      value={p.serviceStatus === " " ? "" : p.serviceStatus}
                      onChange={(e) => handleProductChange(i, "serviceStatus", e.target.value)}
                      className="border p-2 w-full rounded"
                      autoFocus
                    />
                  ) : null}
                </div>
              </div>
            </>
          )}

          {p.category === "abb_warranty" && (
            <div className="space-y-6">
              {/* Header Badge */}
              <div className="flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50/70 p-3 text-teal-900">
                <span className="material-symbols-outlined text-teal-600">verified_user</span>
                <div>
                  <h4 className="text-sm font-bold">ABB Warranty Complaint Report</h4>
                  <p className="text-xs text-teal-700">Inspection items and measurement results as per ABB standard format.</p>
                </div>
              </div>

              {/* SECTION 1: Basic Information */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">info</span>
                  Basic Equipment &amp; Service Information
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex flex-col sm:col-span-2 lg:col-span-3">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1 flex items-center gap-1">
                      Nature of Complaint <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      value={p.natureOfComplaint || ""}
                      placeholder="Describe the nature of complaint reported"
                      className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "natureOfComplaint", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Product
                    </label>
                    <input
                      value={p.product || p.productName || ""}
                      placeholder="e.g. ACB / MCCB"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => {
                        handleProductChange(i, "product", e.target.value);
                        handleProductChange(i, "productName", e.target.value);
                      }}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Model
                    </label>
                    <input
                      value={p.model || ""}
                      placeholder="e.g. Emax 2 / E2.2"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "model", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Panel Builder
                    </label>
                    <input
                      value={p.panelBuilder || ""}
                      placeholder="e.g. Panel Builder Name"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "panelBuilder", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Serial No
                    </label>
                    <input
                      value={p.serialNo || ""}
                      placeholder="e.g. 1SDA..."
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "serialNo", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Number of Poles
                    </label>
                    <input
                      value={p.pole || ""}
                      placeholder="e.g. 3P / 4P"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "pole", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Frame Size / Rating
                    </label>
                    <input
                      value={p.frameRating || p.rating || ""}
                      placeholder="e.g. E2.2 / 1600A"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "frameRating", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Commissioning Date
                    </label>
                    <input
                      type="date"
                      value={toInputDate(p.commissioningDate || "")}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "commissioningDate", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Visit Date
                    </label>
                    <input
                      type="date"
                      value={toInputDate(p.visitDate || form.visitDate || "")}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "visitDate", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Number of Operations per Day
                    </label>
                    <input
                      value={p.operationsPerDay || ""}
                      placeholder="e.g. 5 to 10"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "operationsPerDay", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col sm:col-span-2 lg:col-span-3">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      More Information
                    </label>
                    <textarea
                      rows={2}
                      value={p.additionalInfo || ""}
                      placeholder="Enter additional details or notes"
                      className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "additionalInfo", e.target.value)}
                    />
                  </div>
                </div>

                {renderAdditionalBoxes(p, i)}
              </div>

              {/* SECTION 2: Coils, Motor & Release */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">settings_input_component</span>
                  Coils, Motor &amp; Trip Release
                </h4>

                <div>
                  <label className="font-bold text-xs uppercase text-slate-600 block mb-2">
                    Coils &amp; Spring Charging Motor Checks
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-lg border border-slate-200">
                    {accessoryOptions.map((accessory) => (
                      <div
                        key={accessory.label}
                        className="rounded-lg border border-slate-200 bg-white p-3 shadow-2xs"
                      >
                        <label className="flex items-center gap-2 cursor-pointer hover:text-teal-700 font-medium">
                          <input
                            type="checkbox"
                            checked={(p.checks || []).includes(accessory.label)}
                            onChange={() => handleCheckbox(i, accessory.label)}
                          />
                          {accessory.label}
                        </label>

                        {(p.checks || []).includes(accessory.label) && (
                          <div className="mt-3 pl-6 space-y-2">
                            <div>
                              <p className="text-xs font-semibold text-slate-700 mb-1">Current type:</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {["AC", "DC"].map((typeOption) => (
                                  <label key={`${accessory.label}-${typeOption}`} className="flex items-center gap-1.5 text-xs text-slate-600">
                                    <input
                                      type="checkbox"
                                      checked={p[accessory.typeField] === typeOption}
                                      onChange={() => handleAccessoryCurrentTypeChange(i, accessory.typeField, typeOption)}
                                    />
                                    {typeOption}
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-semibold text-slate-700 mb-1">Choose voltage:</p>
                              <div className="flex flex-wrap gap-x-3 gap-y-1">
                                {accessory.options.map((volt) => (
                                  <label key={`${accessory.label}-${volt}`} className="flex items-center gap-1 text-xs text-slate-600">
                                    <input
                                      type="checkbox"
                                      checked={(p[accessory.field] || []).includes(volt)}
                                      onChange={() => handleVoltageCheckbox(i, accessory.field, volt)}
                                    />
                                    {volt}
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-semibold text-slate-700 mb-1">Status:</p>
                              <select
                                value={p[accessory.statusField] || ""}
                                onChange={(e) => handleProductChange(i, accessory.statusField, e.target.value)}
                                className="w-full rounded border border-slate-300 p-1.5 text-xs bg-white"
                              >
                                <option value="">Select Status</option>
                                <option value="OK">OK</option>
                                <option value="NOT OK">NOT OK</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-2">
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      If Release - Type of Release
                    </label>
                    <input
                      value={p.releaseType || ""}
                      placeholder="e.g. Ekip Dip, Ekip Touch, PR121, Microprocessor"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "releaseType", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 3: Busbar & Cable Installation Inspection */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">cable</span>
                  Busbar &amp; Cable Installation Inspection
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Type of Connection
                    </label>
                    <select
                      value={p.connectionType || "Busbar"}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                      onChange={(e) => handleProductChange(i, "connectionType", e.target.value)}
                    >
                      <option value="Busbar">Busbar</option>
                      <option value="Cable">Cable</option>
                    </select>
                  </div>

                  <div className="flex flex-col sm:col-span-2 lg:col-span-1">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Cable / Busbar Size &amp; Number of Runs
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        value={p.cableBusbarSize || ""}
                        placeholder="Size (e.g. 50x10mm)"
                        className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "cableBusbarSize", e.target.value)}
                      />
                      <input
                        value={p.numberOfRuns || ""}
                        placeholder="Runs (e.g. 2 Runs)"
                        className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "numberOfRuns", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:col-span-2 lg:col-span-1">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Busbar Support
                    </label>
                    <select
                      value={p.busbarSupport || "Available as per ABB recommendation"}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                      onChange={(e) => handleProductChange(i, "busbarSupport", e.target.value)}
                    >
                      <option value="Available as per ABB recommendation">Available as per ABB recommendation</option>
                      <option value="Not Available as per ABB recommendation">Not Available as per ABB recommendation</option>
                      <option value="Provided">Provided</option>
                      <option value="Not Provided">Not Provided</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION 4: Physical Damage & Visual Inspection */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">build</span>
                  Physical &amp; Mechanism Inspection
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase text-slate-700">
                        Visible Damage to Operating Mechanism
                      </label>
                      <select
                        value={p.mechanismDamage || "No"}
                        onChange={(e) => handleProductChange(i, "mechanismDamage", e.target.value)}
                        className="rounded border border-slate-300 bg-white p-1 text-xs font-semibold"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    {p.mechanismDamage === "Yes" && (
                      <input
                        value={p.mechanismDamageDetails || ""}
                        placeholder="Specify mechanism damage details"
                        className="w-full rounded border border-slate-300 bg-white p-2 text-xs focus:border-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "mechanismDamageDetails", e.target.value)}
                      />
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase text-slate-700">
                        Loose Springs / Screws
                      </label>
                      <select
                        value={p.looseSprings || "No"}
                        onChange={(e) => handleProductChange(i, "looseSprings", e.target.value)}
                        className="rounded border border-slate-300 bg-white p-1 text-xs font-semibold"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    {p.looseSprings === "Yes" && (
                      <input
                        value={p.looseSpringsDetails || ""}
                        placeholder="Specify loose springs/screws details"
                        className="w-full rounded border border-slate-300 bg-white p-2 text-xs focus:border-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "looseSpringsDetails", e.target.value)}
                      />
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase text-slate-700">
                        Other Visible Damage
                      </label>
                      <select
                        value={p.otherVisibleDamage || "No"}
                        onChange={(e) => handleProductChange(i, "otherVisibleDamage", e.target.value)}
                        className="rounded border border-slate-300 bg-white p-1 text-xs font-semibold"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    {p.otherVisibleDamage === "Yes" && (
                      <input
                        value={p.otherVisibleDamageDetails || ""}
                        placeholder="Specify other visible damage details"
                        className="w-full rounded border border-slate-300 bg-white p-2 text-xs focus:border-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "otherVisibleDamageDetails", e.target.value)}
                      />
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase text-slate-700">
                        Black / Burn Marks near MCCB Air Vents
                      </label>
                      <select
                        value={p.burnMarksAirVents || "No"}
                        onChange={(e) => handleProductChange(i, "burnMarksAirVents", e.target.value)}
                        className="rounded border border-slate-300 bg-white p-1 text-xs font-semibold"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>
                    {p.burnMarksAirVents === "Yes" && (
                      <input
                        value={p.burnMarksAirVentsDetails || ""}
                        placeholder="Specify burn marks details"
                        className="w-full rounded border border-slate-300 bg-white p-2 text-xs focus:border-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "burnMarksAirVentsDetails", e.target.value)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION 5: Load Details & Remarks */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">electric_bolt</span>
                  Load Details &amp; Remarks
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Load Current (A)
                    </label>
                    <input
                      value={p.loadCurrent || ""}
                      placeholder="e.g. 850 A"
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "loadCurrent", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Type of Load
                    </label>
                    <select
                      value={p.loadType || "Motor"}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                      onChange={(e) => handleProductChange(i, "loadType", e.target.value)}
                    >
                      <option value="Lighting">Lighting</option>
                      <option value="Motor">Motor</option>
                      <option value="HVAC">HVAC</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {p.loadType === "Other" && (
                    <div className="flex flex-col">
                      <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                        Other Load Type
                      </label>
                      <input
                        value={p.loadTypeOther || ""}
                        placeholder="Enter load type"
                        className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none"
                        onChange={(e) => handleProductChange(i, "loadTypeOther", e.target.value)}
                      />
                    </div>
                  )}

                  <div className="flex flex-col sm:col-span-2 lg:col-span-3">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Remarks
                    </label>
                    <textarea
                      rows={2}
                      value={p.remarks || ""}
                      placeholder="Enter remarks"
                      className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "remarks", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 6: Continuity Test (OK / NOT OK) */}
              <div className="rounded-xl border border-teal-200 bg-teal-50/30 p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-teal-900 border-b border-teal-200 pb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white">✓</span>
                    Continuity Test (OK / NOT OK)
                  </div>
                  <span className="text-[11px] font-medium text-teal-700">Select continuity status for each phase</span>
                </h4>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "R – R", field: "resistanceRR" },
                    { label: "Y – Y", field: "resistanceYY" },
                    { label: "B – B", field: "resistanceBB" },
                    { label: "N – N", field: "resistanceNN" }
                  ].map(({ label, field }) => {
                    const currentVal = String(p[field] || "").trim().toUpperCase();
                    const isOk = currentVal === "OK" || currentVal === "OKAY";
                    const isNotOk = currentVal === "NOT OK" || currentVal === "NOT OKAY" || currentVal === "NO OK";

                    return (
                      <div key={field} className="rounded-xl border border-teal-200 bg-white p-3 text-center shadow-xs">
                        <span className="block text-xs font-bold text-slate-800 mb-2">{label}</span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleProductChange(i, field, isOk ? "" : "OK")}
                            className={`py-1.5 px-2 rounded-lg text-xs font-bold border transition-all ${
                              isOk
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                                : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300"
                            }`}
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            onClick={() => handleProductChange(i, field, isNotOk ? "" : "NOT OK")}
                            className={`py-1.5 px-2 rounded-lg text-xs font-bold border transition-all ${
                              isNotOk
                                ? "bg-rose-600 text-white border-rose-600 shadow-xs"
                                : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                            }`}
                          >
                            NOT OK
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* SECTION 7: Observations & Action Taken */}
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 border-b pb-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-base">assignment</span>
                  Observations &amp; Action Taken
                </h4>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1 flex items-center gap-1">
                      Observation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={p.observation || ""}
                      placeholder="Detail physical/electrical observations during inspection"
                      className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "observation", e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1 flex items-center gap-1">
                      Action Taken <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={4}
                      value={p.actionTaken || ""}
                      placeholder="Detail corrective actions / service carried out"
                      className="w-full rounded-lg border border-slate-300 p-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      onChange={(e) => handleProductChange(i, "actionTaken", e.target.value)}
                    />
                  </div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex flex-col w-full sm:w-64">
                    <label className="text-xs font-bold uppercase text-slate-600 mb-1">
                      Product Status
                    </label>
                    <select
                      value={p.serviceStatus || p.status || "Completed"}
                      className="w-full rounded-lg border border-slate-300 p-2 text-sm focus:border-blue-500 outline-none bg-white font-semibold"
                      onChange={(e) => {
                        handleProductChange(i, "serviceStatus", e.target.value);
                        handleProductChange(i, "status", e.target.value);
                      }}
                    >
                      <option value="Completed">Completed</option>
                      <option value="Pending">Pending</option>
                      <option value="Re-Visit Required">Re-Visit Required</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {p.category === "others" && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase text-blue-900 mb-1 flex items-center gap-1">
                    Serial No
                  </label>
                  <input
                    value={p.serialNo || ""}
                    placeholder="Type or select serial number"
                    className="w-full rounded border-2 border-blue-200 p-2 font-mono text-sm uppercase focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    list={`serial-suggestions-${i}`}
                    onChange={(e) => handleProductChange(i, "serialNo", e.target.value)}
                  />
                  <datalist id={`serial-suggestions-${i}`}>
                    {customerProducts.map((cp, cpIdx) => (
                      <option key={cpIdx} value={cp.product?.serialNo || ""}>
                        {cp.product?.productName || cp.product?.product || "Product"}
                      </option>
                    ))}
                  </datalist>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Product</label>
                  <input value={p.product || ""} placeholder="Product" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"product",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Make</label>
                  <input value={p.make || ""} placeholder="Make" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"make",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Model</label>
                  <input value={p.model || ""} placeholder="Model" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"model",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Panel Area</label>
                  <input
                    value={p.panelArea || ""}
                    placeholder="Panel Area"
                    className="w-full rounded border p-2"
                    list={`panel-area-others-suggestions-${i}`}
                    onChange={(e)=>handleProductChange(i,"panelArea",e.target.value)}
                  />
                  <datalist id={`panel-area-others-suggestions-${i}`}>
                    {panelAreaSuggestions.map((area, areaIdx) => (
                      <option key={areaIdx} value={area} />
                    ))}
                  </datalist>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">No of Poles</label>
                  <input value={p.pole || ""} placeholder="No of Poles" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"pole",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Rating</label>
                  <input value={p.rating || ""} placeholder="Rating" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"rating",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Voltage</label>
                  <input value={p.voltage || ""} placeholder="Voltage" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"voltage",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Speed</label>
                  <input value={p.speed || ""} placeholder="Speed" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"speed",e.target.value)} />
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-bold uppercase mb-1">Additional Info</label>
                  <input value={p.additionalInfo || ""} placeholder="Additional Info" className="w-full rounded border p-2" onChange={(e)=>handleProductChange(i,"additionalInfo",e.target.value)} />
                </div>
              </div>

              {p.hasHistory && (
                <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50/50 p-4">
                  <h3 className="text-xs font-bold text-blue-900 mb-3 flex items-center gap-1.5 uppercase tracking-wide">
                    <span className="material-symbols-outlined text-lg">history</span>
                    Asset Service & Component History (Serial: {p.serialNo})
                  </h3>
                  
                  <div className="flex border-b border-blue-200 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "service")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "service" || !p.activeHistoryTab
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Service History ({p.serviceHistory?.length || 0})
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "components")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "components"
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Component History ({p.componentHistory?.length || 0})
                    </button>
                    <button
                      type="button"
                      onClick={() => handleProductHistoryTabChange(i, "prev-test")}
                      className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition ${
                        p.activeHistoryTab === "prev-test"
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      Previous Test Reference
                    </button>
                  </div>

                  {(p.activeHistoryTab === "service" || !p.activeHistoryTab) && (
                    <div className="space-y-3">
                      {(!p.serviceHistory || p.serviceHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No previous service visits found.</p>
                      ) : (
                        <div className="divide-y divide-blue-100 max-h-[250px] overflow-y-auto pr-1">
                          {p.serviceHistory.map((h, idx) => (
                            <div key={idx} className="py-2.5 first:pt-0 last:pb-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-bold text-blue-900">{h.reportId} ({fromInputDate(h.visitDate)})</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  String(h.serviceStatus).toLowerCase().includes("ok") || String(h.serviceStatus).toLowerCase().includes("satisfactorily")
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-rose-100 text-rose-700"
                                }`}>{h.serviceStatus}</span>
                              </div>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Engineer:</span> {h.engineer}</p>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Observation:</span> {h.observation}</p>
                              <p className="text-xs text-slate-600"><span className="font-semibold text-slate-700">Action:</span> {h.actionTaken}</p>
                              {h.remarks && <p className="text-xs text-slate-500 italic mt-0.5">Remarks: "{h.remarks}"</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {p.activeHistoryTab === "components" && (
                    <div>
                      {(!p.componentHistory || p.componentHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No component replacement records found.</p>
                      ) : (
                        <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-blue-100/50 text-blue-950 sticky top-0">
                              <tr>
                                <th className="px-2 py-1.5 font-bold">Component</th>
                                <th className="px-2 py-1.5 font-bold">Status</th>
                                <th className="px-2 py-1.5 font-bold text-center">Identified Date</th>
                                <th className="px-2 py-1.5 font-bold text-center">Replaced Date</th>
                                <th className="px-2 py-1.5 font-bold">Visit</th>
                                <th className="px-2 py-1.5 font-bold">Details</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100 bg-white">
                              {p.componentHistory.map((c, idx) => (
                                <tr key={idx} className="hover:bg-blue-100/10">
                                  <td className="px-2 py-2 font-semibold text-blue-900">{c.name}</td>
                                  <td className="px-2 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                      c.status === "Replaced"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : c.status === "To Be Replaced" || c.status === "Pending"
                                        ? "bg-rose-100 text-rose-700"
                                        : "bg-slate-100 text-slate-700"
                                    }`}>{c.status}</span>
                                  </td>
                                  <td className="px-2 py-2 text-center text-slate-600">{fromInputDate(c.identifiedDate) || "-"}</td>
                                  <td className="px-2 py-2 text-center text-slate-600">{fromInputDate(c.replacedDate) || "-"}</td>
                                  <td className="px-2 py-2 font-mono text-[10px] text-blue-800">{c.reportId}</td>
                                  <td className="px-2 py-2 text-slate-500 max-w-[150px] truncate" title={`${c.engineer}: ${c.remarks}`}>
                                    <span className="font-semibold text-slate-700">{c.engineer}:</span> {c.remarks || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {p.activeHistoryTab === "prev-test" && (
                    <div className="space-y-3 text-xs">
                      {(!p.serviceHistory || p.serviceHistory.length === 0) ? (
                        <p className="text-xs text-slate-500 italic">No previous test data reference found.</p>
                      ) : (() => {
                        const latestTest = p.serviceHistory[p.serviceHistory.length - 1];
                        const t = latestTest.testData || {};
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-bold text-blue-900 mb-1.5 border-b border-blue-100 pb-0.5">Coil Measurements (Latest)</h4>
                              <ul className="space-y-1 text-slate-600">
                                <li><span className="font-semibold text-slate-700">Motor Voltage/Status:</span> {t.motorVoltages?.join(", ") || "-"} ({t.motorStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">Shunt (YO) Voltage/Status:</span> {t.yoVoltages?.join(", ") || "-"} ({t.yoStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">Closing (YC) Voltage/Status:</span> {t.ycVoltages?.join(", ") || "-"} ({t.ycStatus || "-"})</li>
                                <li><span className="font-semibold text-slate-700">UV Release Voltage/Status:</span> {t.uvVoltages?.join(", ") || "-"} ({t.uvStatus || "-"})</li>
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-bold text-blue-900 mb-1.5 border-b border-blue-100 pb-0.5">LSIG Settings (Latest)</h4>
                              {(!t.lsig || t.lsig.length === 0) ? (
                                <p className="text-slate-500 italic">No LSIG parameters recorded.</p>
                              ) : (
                                <ul className="space-y-1 text-slate-600">
                                  {t.lsig.map((row, rIdx) => (
                                    <li key={rIdx}>
                                      <span className="font-semibold text-slate-700">{row.parameter || row.param || "-"}:</span> {row.setting || "-"} (Measured: {row.measured || "-"}, Result: {row.result || "-"})
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">

              <div className="sm:col-span-2">
                {renderAdditionalBoxes(p, i)}
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase mb-1">Warranty</label>
                <select value={p.warranty || ""} className="border p-2 w-full rounded" onChange={(e)=>handleProductChange(i,"warranty",e.target.value)}>
                  <option value="">Select Warranty</option>
                  <option>In Warranty</option>
                  <option>Out of Warranty</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase mb-1">Observation</label>
                <textarea value={p.observation || ""} placeholder="Observation" className="border p-2 w-full rounded h-20" onChange={(e)=>handleProductChange(i,"observation",e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase mb-1">Action Taken</label>
                <textarea value={p.actionTaken || ""} placeholder="Action Taken" className="border p-2 w-full rounded h-20" onChange={(e)=>handleProductChange(i,"actionTaken",e.target.value)} />
              </div>

              <div className="sm:col-span-2 flex flex-col gap-2">
                <label className="text-xs font-bold uppercase mb-1">Status After Visit</label>
                <select 
                  value={
                    ["Working satisfactorily", "Spare Parts Required For Safe Operation", "Repair Required"].includes(p.status) 
                      ? p.status 
                      : (p.status ? "Other" : "")
                  } 
                  className="border p-2 w-full rounded" 
                  onChange={(e)=>{
                    if (e.target.value === "Other") {
                      handleProductChange(i, "status", " ");
                    } else {
                      handleProductChange(i, "status", e.target.value);
                    }
                  }}
                >
                  <option value="">Status after visit</option>
                  <option>Working satisfactorily</option>
                  <option>Spare Parts Required For Safe Operation</option>
                  <option>Repair Required</option>
                  <option value="Other">Other</option>
                </select>
                {p.status && !["Working satisfactorily", "Spare Parts Required For Safe Operation", "Repair Required"].includes(p.status) ? (
                  <input
                    type="text"
                    placeholder="Please specify"
                    value={p.status === " " ? "" : p.status}
                    onChange={(e) => handleProductChange(i, "status", e.target.value)}
                    className="border p-2 w-full rounded"
                    autoFocus
                  />
                ) : null}
              </div>

              <div className="sm:col-span-2 flex flex-col mt-4">
                <label className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                  <span className="material-symbols-outlined text-lg text-rose-500">warning_amber</span>
                  Current Defective Components (To Be Replaced)
                </label>
                <div className="space-y-3">
                  {(p.componentsToBeReplaced || []).length === 0 ? (
                    <p className="text-xs text-slate-500 italic pl-1">No defective components identified for replacement.</p>
                  ) : (
                    (p.componentsToBeReplaced || []).map((comp, compIdx) => (
                      <div key={`${compIdx}-replace`} className="p-3 bg-rose-50/50 border border-rose-100 rounded-xl space-y-2.5">
                        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-4">
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Component Name</label>
                            <input
                              value={comp.name || ""}
                              placeholder="Component name"
                              className="border p-1.5 text-xs rounded bg-white"
                              list="component-suggestions"
                              onChange={(e) => updateComponentToBeReplaced(i, compIdx, "name", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Status</label>
                            <select
                              value={comp.status || "To Be Replaced"}
                              className="border p-1.5 text-xs rounded bg-white font-semibold text-rose-700"
                              onChange={(e) => updateComponentToBeReplaced(i, compIdx, "status", e.target.value)}
                            >
                              <option value="To Be Replaced">To Be Replaced</option>
                              <option value="Pending">Pending</option>
                              <option value="Replaced">Replaced (Move to Replaced Section)</option>
                              <option value="OK / No Replacement Required">OK / No Replacement Required</option>
                              <option value="Removed / No Longer Required">Removed / No Longer Required</option>
                            </select>
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Identified Date</label>
                            <input
                              value={fromInputDate(comp.identifiedDate) || ""}
                              className="border p-1.5 text-xs rounded bg-slate-50 text-slate-500"
                              readOnly
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Remarks</label>
                            <input
                              value={comp.remarks || ""}
                              placeholder="Remarks..."
                              className="border p-1.5 text-xs rounded bg-white"
                              onChange={(e) => updateComponentToBeReplaced(i, compIdx, "remarks", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeComponentToBeReplaced(i, compIdx)}
                            className="px-2.5 py-1 text-xs rounded bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => addComponentToBeReplaced(i)}
                    className="self-start px-4 py-2 rounded bg-rose-100 hover:bg-rose-200 text-rose-700 font-semibold text-xs transition"
                  >
                    + Add Defective Component
                  </button>
                </div>
              </div>

              <div className="sm:col-span-2 flex flex-col mt-6 border-t border-slate-100 pt-6">
                <label className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1">
                  <span className="material-symbols-outlined text-lg text-emerald-500">check_circle</span>
                  Components Replaced During This Visit
                </label>
                <div className="space-y-3">
                  {(p.componentsReplaced || []).length === 0 ? (
                    <p className="text-xs text-slate-500 italic pl-1">No components replaced during this visit yet.</p>
                  ) : (
                    (p.componentsReplaced || []).map((comp, compIdx) => (
                      <div key={`${compIdx}-new`} className="p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl space-y-2">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Component Name</label>
                            <input
                              value={comp.name || ""}
                              placeholder="Component name"
                              className="border p-1.5 text-xs rounded bg-white font-semibold"
                              list="component-suggestions"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "name", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Previous Part / Serial</label>
                            <input
                              value={comp.previousPart || ""}
                              placeholder="Old part details"
                              className="border p-1.5 text-xs rounded bg-white"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "previousPart", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">New Part / Serial</label>
                            <input
                              value={comp.newPart || ""}
                              placeholder="New part details"
                              className="border p-1.5 text-xs rounded bg-white"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "newPart", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Quantity</label>
                            <input
                              type="number"
                              value={comp.quantity || 1}
                              className="border p-1.5 text-xs rounded bg-white font-bold"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "quantity", Number(e.target.value))}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Replacement Date</label>
                            <input
                              type="date"
                              value={comp.replacementDate || ""}
                              className="border p-1.5 text-xs rounded bg-white"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "replacementDate", e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col sm:col-span-2">
                            <label className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Remarks / Cost / Spare Info</label>
                            <input
                              value={comp.remarks || ""}
                              placeholder="Cost details, warranty or general remarks"
                              className="border p-1.5 text-xs rounded bg-white"
                              onChange={(e) => updateComponentReplaced(i, compIdx, "remarks", e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeComponentReplaced(i, compIdx)}
                            className="px-2.5 py-1 text-xs rounded bg-red-100 text-red-700 font-semibold hover:bg-red-200 transition"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => addComponentReplaced(i)}
                    className="self-start px-4 py-2 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold text-xs transition"
                  >
                    + Add Replaced Component
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
            </>
          )}
        </div>
      )})}

      <datalist id="component-suggestions">
        {componentSuggestionOptions.map((componentName) => (
          <option key={componentName} value={componentName} />
        ))}
      </datalist>

      <div className="mt-6 flex flex-col gap-3 rounded border border-gray-300 bg-gray-100 p-4 shadow-inner">
        <label className="font-bold text-gray-800">Add New Product to Report</label>
        <button
          onClick={addProduct}
          disabled={!productType}
          className="rounded bg-green-600 px-6 py-2 font-bold text-white shadow-md transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
        >
          + Add Product
        </button>
      </div>

      <div className="mt-6 rounded border-t-8 border-blue-800 bg-white p-4 shadow sm:p-6">
        <h3 className="font-bold border-b pb-2 mb-4 text-xl text-blue-900">Final Report Summary</h3>
        <div className="grid grid-cols-1 gap-6">
          <div className="rounded-lg border border-gray-300 p-4">
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1 uppercase tracking-tight">
                Observation / Action Taken
              </label>
              <textarea
                value={form.observation}
                className="border p-3 rounded focus:ring-2 focus:ring-blue-200 min-h-[140px]"
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    observation: e.target.value,
                    actionTaken: e.target.value
                  }))
                }
                placeholder="Enter observation and action taken"
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-300 p-4">
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1 uppercase tracking-tight">
                Customer Feedback
              </label>
              <textarea
                value={form.customerFeedback}
                className="border p-3 rounded focus:ring-2 focus:ring-blue-200 min-h-[120px]"
                onChange={(e) =>
                  setForm((current) => ({
                    ...current,
                    customerFeedback: e.target.value
                  }))
                }
                placeholder="Enter customer feedback"
              />
            </div>
          </div>

          {!canCreateReportNow && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              Travel status must be updated to At Site before an engineer can create this report.
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
            <div>
              <label className="font-bold block mb-2 text-gray-700 text-sm italic">Customer Signature (Landscape)</label>
              <div className="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg overflow-hidden">
                <SignatureCanvas ref={customerSignRef} canvasProps={{ width: 450, height: 150, className: "signature-canvas w-full bg-white" }} />
              </div>
              {existingCustomerSignature && (
                <div className="mt-3 p-2 border rounded-lg bg-white flex flex-col items-center">
                  <img src={existingCustomerSignature} alt="Customer signature" className="h-16 max-w-full object-contain" />
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button type="button" onClick={() => { customerSignRef.current?.clear(); setExistingCustomerSignature(""); }} className="text-xs text-red-600 font-bold uppercase hover:underline">Clear Signature</button>
                {existingCustomerSignature && (
                  <button
                    type="button"
                    onClick={async () => {
                      const rotated = await rotateImageDataUrl(existingCustomerSignature, 90);
                      setExistingCustomerSignature(rotated);
                    }}
                    className="text-xs text-slate-700 font-bold uppercase hover:underline"
                  >
                    Rotate 90°
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSignatureModal("customer")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-slate-800"
                >
                  Open Fullscreen Pad
                </button>
              </div>
            </div>

            <div>
              <label className="font-bold block mb-2 text-gray-700 text-sm italic">Engineer Signature (Landscape)</label>
              <div className="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg overflow-hidden">
                <SignatureCanvas ref={engineerSignRef} canvasProps={{ width: 450, height: 150, className: "signature-canvas w-full bg-white" }} />
              </div>
              {existingEngineerSignature && (
                <div className="mt-3 p-2 border rounded-lg bg-white flex flex-col items-center">
                  <img src={getSignatureUrl(existingEngineerSignature)} alt="Engineer signature" className="h-16 max-w-full object-contain" />
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <button
                  type="button"
                  onClick={() => {
                    engineerSignRef.current?.clear();
                    setExistingEngineerSignature("");
                  }}
                  className="text-xs text-red-600 font-bold uppercase hover:underline"
                >
                  Clear Signature
                </button>
                {existingEngineerSignature && (
                  <button
                    type="button"
                    onClick={async () => {
                      const rotated = await rotateImageDataUrl(existingEngineerSignature, 90);
                      setExistingEngineerSignature(rotated);
                    }}
                    className="text-xs text-slate-700 font-bold uppercase hover:underline"
                  >
                    Rotate 90°
                  </button>
                )}
                {profileEngineerSignature && (
                  <button
                    type="button"
                    onClick={() => {
                      engineerSignRef.current?.clear();
                      setExistingEngineerSignature(profileEngineerSignature);
                    }}
                    className="text-xs text-blue-700 font-bold uppercase hover:underline"
                  >
                    Use Profile Signature
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSignatureModal("engineer")}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white transition hover:bg-slate-800"
                >
                  Open Fullscreen Pad
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button disabled={saving || !canCreateReportNow || (!isAdminMode && (approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked") && !editAccessGranted)} onClick={submitReport} className="bg-blue-800 text-white px-8 py-4 rounded-xl font-black hover:bg-blue-900 transition-all shadow-xl hover:shadow-2xl text-lg uppercase disabled:bg-gray-400">
              {saving
                ? "Saving Report..."
                : !canCreateReportNow
                ? "Reach Site To Create Report"
                : !isAdminMode && (approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked") && !editAccessGranted
                ? "Report Approved"
                : reportDocId
                ? "Update Final Service Report"
                : "Submit Final Service Report"}
            </button>

            {reportDocId && (
              <button
                type="button"
                disabled={deleting || saving}
                onClick={deleteReport}
                className="bg-red-600 text-white px-8 py-4 rounded-xl font-black hover:bg-red-700 transition-all shadow-xl hover:shadow-2xl text-lg uppercase disabled:bg-red-300"
              >
                {deleting ? "Deleting Report..." : "Delete Report"}
              </button>
            )}
          </div>
        </div>
      </div>

      {signatureModal === "customer" && (
        <SignaturePadModal
          title="Customer Signature"
          existingSignature={existingCustomerSignature}
          onClose={() => setSignatureModal("")}
          onSave={(signature) => {
            customerSignRef.current?.clear();
            setExistingCustomerSignature(signature);
            setSignatureModal("");
          }}
        />
      )}

      {signatureModal === "engineer" && (
        <SignaturePadModal
          title="Engineer Signature"
          existingSignature={existingEngineerSignature}
          onClose={() => setSignatureModal("")}
          onSave={(signature) => {
            engineerSignRef.current?.clear();
            setExistingEngineerSignature(signature);
            setSignatureModal("");
          }}
        />
      )}

    </>
  );

  return isAdminMode ? content : <EngineerLayout>{content}</EngineerLayout>;
}

export default CreateReport;
